import React, { useState, useEffect, useRef } from "react";
import "./AdminDashboard.css";
import "../adminSharedFromChat.css";
import EmcureLogo from "../../../assets/images/emcure_logo.svg";
import adminService from "../../../services/adminService";
import Pagination from "../../Common/Pagination";
import HighlightText from "../../Common/HighlightText";
import MarkdownRenderer from "../../Common/MarkdownRenderer";
import DashboardPanelSwitcher from "../../Common/DashboardPanelSwitcher";
import SqlResultDataTable, {
  SqlResultDataTableFullViewModal,
  SqlResultViewSwitcher,
} from "../../Common/SqlResultDataTable";
import EmcureModalShell from "../../Common/EmcureModalShell/EmcureModalShell";
import "../../Common/EmcureModalShell/EmcureModalShell.css";
import {
  EmcureModalChartBody,
  EmcureAdminInlineChartPlot,
  ChartPdfExportTable,
} from "../../Common/EmcureDataCharts";
import {
  getChartTypeOptionsForPointCount,
  humanizeColumnLabel,
} from "../../Common/EmcureDataCharts/emcureChartConstants";
import { resolveChatChartType } from "../../ChatPage/ChatSectionChartHelpers";
import {
  AdminCardCacheEntries,
  AdminCardCacheHits,
  AdminCardPromptCacheHits,
  AdminCardTotalFeedback,
  AdminCardTotalMessages,
  AdminCardTotalUsers,
  IconChartBar,
  IconChartLine,
  IconChartPie,
  IconCheck,
  IconChevronDown,
  IconChevronDownBold,
  IconClose,
  IconCopy,
  IconDownload,
  IconExternalExpand,
  IconLogout,
  IconMessageBubbleStroke,
  IconSearch,
  IconSqlBracketsWhite,
  IconThumbDownFilled,
  IconThumbUpFilled,
  IconX,
} from "../../Common/EmcureReactIcons";
import {
  ChartContainer,
  AreaChartComponent,
  BarChartComponent,
  LineChartComponent,
  ChartConfig,
} from "../../ui/chart";
import AdminSidebar from "../AdminSidebar/AdminSidebar";
import SearchSection from "../SearchSection/SearchSection";
import SessionsTable from "../SessionsTable/SessionsTable";
import FeedbacksTable from "../FeedbacksTable/FeedbacksTable";
import botIcon from "../../../assets/images/icon-chat-message.svg";
import {
  applyAdminSearchParams,
  filterAdminListBySearch,
} from "../../../utils/adminSearchUtils";
import profileIcon from "../../../assets/images/userIcon.png";
import iconProfile from "../../../assets/images/icon_profile.svg";
import { getMsalInstance } from "../../../msalConfig";
import { clearUserInfo } from "../../../utils/userUtils";
import { tokenRefreshService } from "../../../services/tokenRefreshService";
import {
  getUserName,
  getUserRole,
  getUserEmail,
  getUserId,
  getAdminAccess,
} from "../../../utils/userUtils";
import { useNavigate } from "react-router-dom";
import { formatChartAxisDateValue as formatDateValue } from "../../../utils/formatChartAxisDate";
import {
  prepareVisualizationData,
  transformChartData,
} from "../../../utils/dataTransformers";
import {
  enrichChartsFromRawResults,
  filterDisplayableCharts,
  hasDisplayableCharts,
  isChartPointValid,
  resolveSuggestedChartsFromMessage,
} from "../../../utils/chartDataBuilders";
import { jsPDF } from "jspdf";
import { toast } from "react-toastify";
import autoTable from "jspdf-autotable";
import chatService from "../../../services/chatService";
import { downloadCsv, getCsvExportOptions } from "../../ChatPage/chatSectionCsvUtils";
import {
  applyBufferedTableLoadMore,
  buildInitialTablePagination,
  buildTableMetaFromSource,
  buildTablePaginationEntry,
  extractTablePaginationSources,
  getLoadMoreBatchSize,
  parseTableMessageIndex,
  processLoadMoreResponse,
  resolveTableFooterState,
  resolveTableLoadOffset,
  TABLE_PAGE_SIZE,
} from "../../../utils/tablePaginationUtils";
import { applyTableSortResult } from "../../../utils/applyTableSortResult";
import { fetchSortedTableViaExecuteSql } from "../../../utils/fetchSortedTableViaExecuteSql";
import { resolveSortRowCount } from "../../../utils/sortLimitOptions";
import { resolveExecuteSqlQuery } from "../../../utils/resolveExecuteSqlQuery";
import { parseExecuteSqlResponse } from "../../../utils/parseExecuteSqlResponse";
import { getTableColumnSchema } from "../../../utils/normalizeTableRows";
import {
  captureChartCardImage,
  fitChartImageToPdf,
} from "../../../utils/captureChartCardImage";
import { useDateRange } from "../useDateRange";
import DateRangePicker from "../DateRangePicker/DateRangePicker";
import TipsAndUpdatesRoundedIcon from '@mui/icons-material/TipsAndUpdatesRounded';
import QuestionAnswerRoundedIcon from '@mui/icons-material/QuestionAnswerRounded';
import SemanticSettings from "../../Semantic/SemanticSettings";
import AccessManagerPanel from "../../AccessManager";
import { isAccessManagerEnabled } from "../../AccessManager/featureFlags";

const AdminDashboard = () => {
    const navigate = useNavigate();

  const [showMessagesModal, setShowMessagesModal] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [sessionMessages, setSessionMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [messageSearchQuery, setMessageSearchQuery] = useState("");
  const [showSubQueryDropdown, setShowSubQueryDropdown] = useState({});
  const [sessionSearchQuery, setSessionSearchQuery] = useState("");
  const [feedbackSearchQuery, setFeedbackSearchQuery] = useState("");
  const [showFeedbackMessageModal, setShowFeedbackMessageModal] = useState(false);
  const [selectedFeedbackMessage, setSelectedFeedbackMessage] = useState(null);
  const [fullViewMessageChart, setFullViewMessageChart] = useState(null);
  const [fullViewTable, setFullViewTable] = useState(null);
  const [fullViewTableLoading, setFullViewTableLoading] = useState(false);
  const [openTableExportDropdown, setOpenTableExportDropdown] = useState(null);
  const [exportCsvLoadingKey, setExportCsvLoadingKey] = useState(null);
  const [openTableSortDropdown, setOpenTableSortDropdown] = useState(null);
  const [tableSortLoadingKey, setTableSortLoadingKey] = useState(null);
  const [tableActiveSortByMsg, setTableActiveSortByMsg] = useState({});
  const [tableResultViewModes, setTableResultViewModes] = useState({});
  const [tablePagination, setTablePagination] = useState({});
  const [loadingPagination, setLoadingPagination] = useState({});
  const sessionMessagesRef = useRef(sessionMessages);
  const tablePaginationRef = useRef(tablePagination);
  const loadingPaginationRef = useRef(loadingPagination);
  const chartVizPrefetchInFlightRef = useRef(new Set());

  useEffect(() => {
    sessionMessagesRef.current = sessionMessages;
  }, [sessionMessages]);

  useEffect(() => {
    tablePaginationRef.current = tablePagination;
  }, [tablePagination]);

  useEffect(() => {
    loadingPaginationRef.current = loadingPagination;
  }, [loadingPagination]);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentFeedbackPage, setCurrentFeedbackPage] = useState(1);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [expandedCards, setExpandedCards] = useState(new Set(["Total Messages", "Total Users", "Cache Hits", "Total Feedback", "Prompt Cache Hits"]));
  const messagesExportItemRefs = useRef({});
  const messagesExportListRef = useRef(null);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [dailyBreakdown, setDailyBreakdown] = useState([]);
  const [stats, setStats] = useState({
    total_sessions: 0,
    total_users: 0,
    total_messages: 0,
    total_feedback: 0,
    total_cache_hits: 0,
    total_cache_entries: 0,
    total_cache_hits_prompt: 0,
    total_tokens_saved: 0,
  });
  const [tablePageByMessage, setTablePageByMessage] = useState({});
  const [sessionsData, setSessionsData] = useState({
    total: 0,
    page: 1,
    page_size: 10,
    total_pages: 1,
    sessions: [],
  });
  const [feedbacksData, setFeedbacksData] = useState({
    total: 0,
    page: 1,
    page_size: 10,
    total_pages: 1,
    feedbacks: [],
  });
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [loadingFeedbacks, setLoadingFeedbacks] = useState(false);
  const [activeMenu, setActiveMenu] = useState("dashboard");
  const profileDropdownRef = useRef(null);
  const itemsPerPage = 10;
  const [copiedSqlIndex, setCopiedSqlIndex] = useState(null);
  const [msgChartTypeOverrides, setMsgChartTypeOverrides] = useState({}); // { 'msgIdx-chartIdx': 'bar'|'pie'|'line' }
  const [openMsgChartTypeDropdown, setOpenMsgChartTypeDropdown] = useState(null); // chartKey or null

  // Shared date range for Dashboard, Sessions, Feedbacks - applied via onApply
  const dateRange = useDateRange((iso, days, startTime, endTime) => {
    setSessionSearchQuery("");
    fetchDashboard(iso, days, startTime, endTime);
    fetchDailyData(iso, days, startTime, endTime);
    fetchFeedbacks(1, days, startTime, endTime);
    fetchSessions(1, iso, days, startTime, endTime);
  });
  const {
    selectedEndDate,
    selectedDays,
    selectedStartTime,
    selectedEndTime,
    getCurrentRange,
    getDateRangeLabel,
    isDatePickerOpen,
    openPicker,
    visibleMonth,
    calendarSelectionDays,
    pendingDateForTime,
    buildMonthMatrix,
    isDateInRange,
    isRangeBoundary,
    handleCalendarDayClick,
    handleApplyDateWithTime,
    handleQuickDateFilter,
    handlePrevMonth,
    handleNextMonth,
    setSelectedStartTime,
    setSelectedEndTime,
    setSelectedEndDate,
    setSelectedDays,
    setVisibleMonth,
  } = dateRange;

  // Fetch dashboard KPIs
  async function fetchDashboard(endDate, days = 7, startTime = "00:00:00", endTime = "23:59:59") {
    try {
      setLoadingDashboard(true);
      const data = await adminService.getDashboard({ days, selectedDate: endDate, startTime, endTime });
            
      if (!data) {
        setDailyBreakdown([]);
        return;
      }
      
      setDashboardStats(data);

      // Use summary data if available, otherwise use individual fields
      if (data.summary) {
        setStats((prev) => ({
          ...prev,
          total_sessions: data.summary.total_sessions ?? 0,
          total_users: data.summary.total_users ?? 0,
          total_messages: data.summary.total_messages ?? 0,
          total_feedback: data.summary.total_feedback ?? 0,
          total_cache_hits: data.summary.total_cache_hits ?? 0,
          total_cache_entries: data.summary.total_cache_entries ?? 0,
          total_cache_hits_prompt: data.summary.total_cache_hits_prompt ?? 0,
          total_tokens_saved: data.summary.total_tokens_saved ?? 0,
        }));
      } else {
        setStats((prev) => ({
          ...prev,
          total_sessions: data.total_sessions ?? 0,
          total_users: data.total_users ?? 0,
          total_messages: data.total_messages ?? 0,
          total_feedback: data.total_feedback ?? 0,
          total_cache_hits: data.total_cache_hits ?? 0,
          total_cache_entries: data.total_cache_entries ?? 0,
          total_cache_hits_prompt: data.total_cache_hits_prompt ?? 0,
          total_tokens_saved: data.total_tokens_saved ?? 0,
        }));
      }
    } catch (error) {
      setDailyBreakdown([]);
    } finally {
      setLoadingDashboard(false);
    }
  }

  async function fetchDailyData(endDate, days = 7, startTime = "00:00:00", endTime = "23:59:59") {
    try {
      const data = await adminService.getDashboardDailyData({ days, selectedDate: endDate, startTime, endTime });
      
      if (!data) {
        setDailyBreakdown([]);
        return;
      }
      
      // Store daily breakdown data - check multiple possible locations
      let dailyData = [];
      if (data.daily_breakdown && Array.isArray(data.daily_breakdown)) {
        dailyData = data.daily_breakdown;
      } else if (data.data && Array.isArray(data.data)) {
        dailyData = data.data;
      } else if (Array.isArray(data)) {
        dailyData = data;
      } else {
      }
      
      setDailyBreakdown(dailyData);
    } catch (error) {
      setDailyBreakdown([]);
    }
  }

  useEffect(() => {
    if (!openTableExportDropdown) return;
    const handler = (e) => {
      if (!e.target.closest(".table-export-dropdown-wrapper")) {
        setOpenTableExportDropdown(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [openTableExportDropdown]);

  useEffect(() => {
    if (!openTableSortDropdown) return;
    const handler = (e) => {
      if (
        !e.target.closest(".table-sort-dropdown-wrapper") &&
        !e.target.closest(".table-sort-dropdown-menu")
      ) {
        setOpenTableSortDropdown(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [openTableSortDropdown]);

  useEffect(() => {
    // Get today's date in YYYY-MM-DD format
    const today = new Date();
    const todayISO = today.toISOString().slice(0, 10);
    
    // Fetch both dashboard and daily data
    const initializeDashboard = async () => {
      try {
        await Promise.all([
          fetchDashboard(todayISO, 7, "00:00:00", "23:59:59"),
          fetchDailyData(todayISO, 7, "00:00:00", "23:59:59")
        ]);
      } catch (error) {
      }
    };
    
    initializeDashboard();
  }, []);

  // When the backend returns a new date_range, keep the visible calendar month
  // in sync with the end_date so the highlighted range is always in view.
  useEffect(() => {
    if (dashboardStats && dashboardStats.date_range) {
      const end = new Date(dashboardStats.date_range.end_date);
      // Keep previous month on the left, current (end month) on the right
      const leftMonth = new Date(end.getFullYear(), end.getMonth() - 1, 1);
      setVisibleMonth(leftMonth);
    }
  }, [dashboardStats, setVisibleMonth]);

  // Update selectedEndDate when dashboardStats changes
  useEffect(() => {
    if (dashboardStats && dashboardStats.date_range) {
      setSelectedEndDate(dashboardStats.date_range.end_date);
    }
  }, [dashboardStats, setSelectedEndDate]);

  // Monitor dailyBreakdown changes for chart rendering
  useEffect(() => {
    if (dailyBreakdown && dailyBreakdown.length > 0) {
    } else {
    }
  }, [dailyBreakdown]);

  // Guardrails violations data - fetch when on dashboard
  // Fetch sessions list
  const fetchSessions = async (page, selectedDate, days = 7, startTime = "00:00:00", endTime = "23:59:59", searchId = "") => {
    try {
      setLoadingSessions(true);
      const params = {
        page,
        page_size: itemsPerPage,
        selected_date: selectedDate,
        days: days,
        start_time: startTime,
        end_time: endTime,
      };

      applyAdminSearchParams(params, searchId);

      const data = await adminService.getSessions(params);
      const sessions = filterAdminListBySearch(data.sessions ?? [], searchId);
      setSessionsData({
        total: data.total ?? 0,
        page: data.page ?? page,
        page_size: data.page_size ?? itemsPerPage,
        total_pages: data.total_pages ?? 1,
        sessions,
      });
    } catch (error) {
      setSessionsData((prev) => ({
        ...prev,
        sessions: [],
      }));
    } finally {
      setLoadingSessions(false);
    }
  };

  const isInitialMount = useRef(true);

  // Debounced search for sessions to trigger server-side filtering
  useEffect(() => {
    // Skip the initial mount fetch as it's handled by the mount useEffect
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const timer = setTimeout(() => {
      // If the search query is empty, we don't necessarily need to refetch here
      // if it was cleared by the date range callback (which already calls fetchSessions).
      // But if the user manually cleared it, we do. To be safe, we always fetch
      // if it's not the initial mount, with a small delay to allow state to settle.
      if (sessionSearchQuery !== undefined) {
        setCurrentPage(1);
        const today = new Date();
        const todayISO = today.toISOString().slice(0, 10);
        fetchSessions(
          1, 
          selectedEndDate || todayISO, 
          selectedDays || 7, 
          selectedStartTime || "00:00:00", 
          selectedEndTime || "23:59:59", 
          sessionSearchQuery
        );
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [sessionSearchQuery, selectedEndDate, selectedDays, selectedStartTime, selectedEndTime]);

  // Fetch feedback list
  const fetchFeedbacks = async (page, days = 7, startTime = "00:00:00", endTime = "23:59:59", searchId = "") => {
    try {
      setLoadingFeedbacks(true);

      const params = {
        page,
        page_size: itemsPerPage,
        selected_date: selectedEndDate || new Date().toISOString().slice(0, 10),
        days,
        start_time: startTime,
        end_time: endTime,
      };

      applyAdminSearchParams(params, searchId);

      const data = await adminService.getFeedbacksDetailed(params);

      // Normalize the response so that pagination in the UI always reflects
      // the backend values (handles numbers or numeric strings).
      const feedbackArray = filterAdminListBySearch(
        data.feedbacks || data.feedback || [],
        searchId
      );

      const coerceNumber = (value, fallback) => {
        const n = Number(value);
        return Number.isFinite(n) && n > 0 ? n : fallback;
      };

      const pageSize = coerceNumber(data.page_size, itemsPerPage || 10);
      const totalItems = coerceNumber(data.total, feedbackArray.length);
      const totalPages = coerceNumber(
        data.total_pages,
        Math.max(1, Math.ceil(totalItems / pageSize))
      );

      setFeedbacksData({
        total: totalItems,
        page: coerceNumber(data.page, page),
        page_size: pageSize,
        total_pages: totalPages,
        feedbacks: feedbackArray,
      });
    } catch (error) {
      setFeedbacksData((prev) => ({
        ...prev,
        feedbacks: [],
      }));
    } finally {
      setLoadingFeedbacks(false);
    }
  };

  const isInitialFeedbackMount = useRef(true);

  // Debounced search for feedbacks to trigger server-side filtering
  useEffect(() => {
    // Skip the initial mount fetch as it's handled by the mount useEffect
    if (isInitialFeedbackMount.current) {
      isInitialFeedbackMount.current = false;
      return;
    }

    const timer = setTimeout(() => {
      if (feedbackSearchQuery !== undefined) {
        setCurrentFeedbackPage(1);
        fetchFeedbacks(
          1, 
          selectedDays || 7, 
          selectedStartTime || "00:00:00", 
          selectedEndTime || "23:59:59", 
          feedbackSearchQuery
        );
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [feedbackSearchQuery, selectedEndDate, selectedDays, selectedStartTime, selectedEndTime]);

  // Initial list fetch (run once on mount)
  useEffect(() => {
    const today = new Date();
    const todayISO = today.toISOString().slice(0, 10);
    fetchSessions(1, todayISO, 7, "00:00:00", "23:59:59");
    fetchFeedbacks(1, 7, "00:00:00", "23:59:59");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: run once on mount only
  }, []);

  // Map field names to stats keys for fallback when no daily data
  const fieldToStatKey = {
    messages: "total_messages",
    users: "total_users",
    feedback: "total_feedback",
    sessions: "total_sessions",
    cache_hits: "total_cache_hits",
    prompt_cache_hits: "total_cache_hits_prompt",
    tokens_saved: "total_tokens_saved",
  };

  // API may return different keys per day (e.g. total_messages, message_count); try these in order
  const fieldToApiKeys = {
    messages: ["messages", "total_messages", "message_count"],
    users: ["users", "total_users", "user_count"],
    feedback: ["feedback", "total_feedback", "feedback_count"],
    sessions: ["sessions", "total_sessions", "session_count"],
    cache_hits: ["cache_hits", "total_cache_hits"],
    prompt_cache_hits: ["prompt_cache_hits", "total_cache_hits_prompt"],
    tokens_saved: ["tokens_saved", "total_tokens_saved"],
    blocked_responses: ["blocked_responses", "blocked_responses_count"],
  };

  // Sum of chart data values so card total matches breakdown (avoids mismatch)
  const sumChartData = (chartData) => {
    if (!chartData || !Array.isArray(chartData)) return 0;
    return chartData.reduce((acc, item) => acc + (Number(item?.value) || 0), 0);
  };

  // Transform daily breakdown data to chart format; fallback to synthetic data from stats when empty
  const transformDailyDataToChart = (fieldName) => {
    if (!dailyBreakdown || dailyBreakdown.length === 0) {
      // Generate fallback chart data from stats so charts still render
      const statKey = fieldToStatKey[fieldName];
      const totalValue = statKey ? (stats[statKey] ?? 0) : 0;
      const days = selectedDays || 7;
      const valuePerDay = Math.round(totalValue / Math.max(days, 1));
      const fallbackData = [];
      let endDate = selectedEndDate ? new Date(selectedEndDate) : new Date();
      if (isNaN(endDate.getTime())) endDate = new Date();
      for (let i = days - 1; i >= 0; i--) {
        const d = new Date(endDate);
        d.setDate(d.getDate() - i);
        fallbackData.push({
          date: d.toLocaleString("en-US", { month: "short", day: "numeric" }),
          value: valuePerDay > 0 ? valuePerDay : (i === 0 ? totalValue : 0),
        });
      }
      return fallbackData.length > 0 ? fallbackData : [{ date: "No data", value: totalValue }];
    }
    
    const apiKeys = fieldToApiKeys[fieldName] || [fieldName];
    const getValue = (day) => {
      for (const key of apiKeys) {
        const v = day[key];
        if (v !== undefined && v !== null && (typeof v === "number" || (typeof v === "string" && v !== ""))) return Number(v) || 0;
      }
      return 0;
    };

    const sortedBreakdown = [...dailyBreakdown].sort((a, b) => {
      const rawA = a?.date ?? a?.month ?? a?.month_date ?? "";
      const rawB = b?.date ?? b?.month ?? b?.month_date ?? "";
      const tA = new Date(rawA).getTime();
      const tB = new Date(rawB).getTime();
      return (isNaN(tA) ? 0 : tA) - (isNaN(tB) ? 0 : tB);
    });

    const chartData = sortedBreakdown.map((day) => {
      if (!day) {
        return null;
      }
      const dateRaw = day.date ?? day.month ?? day.month_date ?? day.date_key;
      if (dateRaw === undefined) {
        return null;
      }
      try {
        let date = new Date(dateRaw);
        if (isNaN(date.getTime()) && typeof dateRaw === "string" && /^\d{4}-\d{2}$/.test(dateRaw.trim())) {
          date = new Date(dateRaw + "-01");
        }
        if (isNaN(date.getTime())) {
          return null;
        }
        const formattedDate = date.toLocaleString("en-US", { month: "short", day: "numeric" });
        return {
          date: formattedDate,
          value: getValue(day),
        };
      } catch (error) {
        return null;
      }
    }).filter(item => item !== null);
    return chartData;
  };

  // For area/line charts, Recharts needs ≥2 points to draw; use at least 2 for display (total stays correct)
  const chartDataForDisplay = (data) =>
    data && data.length === 1 ? [data[0], { ...data[0] }] : data;

  // Generate chart data from daily breakdown
  const messagesChartData = transformDailyDataToChart("messages");
  const usersChartData = transformDailyDataToChart("users");
  const feedbackChartData = transformDailyDataToChart("feedback");
  const totalSessionsChartData = transformDailyDataToChart("sessions");
  const blockedResponsesChartData = transformDailyDataToChart("blocked_responses");
  const cacheHitsChartData = transformDailyDataToChart("cache_hits");
  const cacheHitsPromptChartData = transformDailyDataToChart("prompt_cache_hits");
  const tokensSavedChartData = transformDailyDataToChart("tokens_saved");


  // Emcure color palette for charts (centralized)
  const EMCURE_COLORS = {
    primary: "#B90005",
    primaryDark: "#8B0000",
    accentWarm: "#b90005",
    accentSoft: "#FEE2E2",
  };

  // When we have daily breakdown, use sum of chart data so card total matches breakdown
  const hasBreakdown = dailyBreakdown && dailyBreakdown.length > 0;
  const messagesTotal = hasBreakdown ? sumChartData(messagesChartData) : stats.total_messages;
  const usersTotal = hasBreakdown ? sumChartData(usersChartData) : stats.total_users;
  const cacheHitsTotal = hasBreakdown ? sumChartData(cacheHitsChartData) : stats.total_cache_hits;
  const feedbackTotal = hasBreakdown ? sumChartData(feedbackChartData) : stats.total_feedback;

  // First row cards (3 cards) - Total Messages, Total Users, Cache Hits
  const firstRowCards = [
    {
      title: "Total Messages",
      desc: `${messagesTotal} messages exchanged`,
      value: messagesTotal,
      chartData: messagesChartData,
      chartType: "area",
      color: EMCURE_COLORS.primary,
    },
    {
      title: "Total Users",
      desc: `${usersTotal} registered users`,
      value: usersTotal,
      chartData: usersChartData,
      chartType: "line",
      color: EMCURE_COLORS.primaryDark,
    },
   
  ];

  // Second row cards (2 cards) - Total Feedback, Prompt Cache Hits
  const secondRowCards = [
     {
      title: "Cache Hits",
      desc: `${cacheHitsTotal} cache hits`,
      value: cacheHitsTotal,
      chartData: cacheHitsChartData,
      chartType: "bar",
      color: EMCURE_COLORS.accentWarm,
    },
    {
      title: "Total Feedback",
      desc: `${feedbackTotal} feedback entries`,
      value: feedbackTotal,
      chartData: feedbackChartData,
      chartType: "bar",
      color: EMCURE_COLORS.accentWarm,
    },
    // {
    //   title: "Prompt Cache Hits",
    //   desc: `${stats.total_cache_hits_prompt} prompt cache hits`,
    //   value: stats.total_cache_hits_prompt,
    //   chartData: cacheHitsPromptChartData,
    //   chartType: "line",
    //   color: EMCURE_COLORS.primary,
    // },
  ];

  // Full width card - Tokens Saved
  // const fullWidthCards = [
  //   {
  //     title: "Tokens Saved",
  //     desc: `${stats.total_tokens_saved} tokens saved via cache`,
  //     value: stats.total_tokens_saved,
  //     chartData: tokensSavedChartData,
  //     chartType: "line",
  //     color: EMCURE_COLORS.primaryDark,
  //   },
  // ];

  const rightCards = [];

  // Top 5 sessions for the "Total Sessions" card, derived from the
  // sessions list API (sorted by most recent activity).
  const topSessions = [...(sessionsData.sessions || [])]
    .sort((a, b) => {
      const aTime = new Date(a.last_activity || a.created_at || 0).getTime();
      const bTime = new Date(b.last_activity || b.created_at || 0).getTime();
      return bTime - aTime;
    })
    .slice(0, 5);

  // Map top sessions into the shape used by the existing UI so that
  // we can reuse the same layout without structural changes.
  const topSessionsForCard = topSessions.map((session, index) => ({
    message_id: session.session_id,
    user_name: session.user_id || "Unknown user",
    user_id: session.user_id || "-",
    message_content: `Messages: ${session.message_count ?? 0}`,
    session_id: session.session_id,
    timestamp: session.last_activity || session.created_at,
    message_type: session.is_active ? "Active session" : "Inactive session",
    category: `Session ${index + 1}`,
    status: session.is_active ? "Active" : "Inactive",
  }));

  // Format date helper function
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "-";
    return date.toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "Asia/Kolkata",
    });
  };

  // Format time for messages (just time)
  const formatTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "-";
    return date.toLocaleString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "Asia/Kolkata",
    });
  };

  const safeText = (text) => {
    if (text === null || text === undefined) return "";
    const s = String(text);
    if (s === "undefined" || s === "null") return "";
    return s;
  };

  const parseTooltipMetricValue = (v) => {
    if (v == null) return NaN;
    if (typeof v === "number" && Number.isFinite(v)) return v;
    const cleaned = String(v).replace(/[₹$€,\s]/g, "").trim();
    const n = parseFloat(cleaned);
    return Number.isFinite(n) ? n : NaN;
  };

  const formatPieCategoryLabel = (xv) => {
    const formatted = formatDateValue(xv);
    if (formatted !== xv && formatted !== undefined && formatted !== null) {
      return String(formatted);
    }
    return String(xv ?? "");
  };

  const escapeHtml = (unsafe) => {
    if (unsafe == null) return "";
    return String(unsafe)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  };

  // Format SQL for display: separate query blocks, put clauses on new lines, basic alignment
  const formatSqlForDisplay = (sql) => {
    if (!sql) return "";
    let formatted = String(sql).replace(/\s+/g, " ").trim();

    // Separate multiple query blocks (e.g. {Query 1}, {Query 2}) onto their own lines
    formatted = formatted.replace(/(\{Query\s*\d+\})/gi, "\n\n$1");

    const clausePatterns = [
      { regex: /\s+(SELECT)\b/gi, replacement: "\n$1 " },
      { regex: /\s+(WITH)\b/gi, replacement: "\n$1 " },
      { regex: /\s+(FROM)\b/gi, replacement: "\n$1 " },
      { regex: /\s+(WHERE)\b/gi, replacement: "\n$1 " },
      { regex: /\s+(GROUP BY)\b/gi, replacement: "\n$1 " },
      { regex: /\s+(HAVING)\b/gi, replacement: "\n$1 " },
      { regex: /\s+(ORDER BY)\b/gi, replacement: "\n$1 " },
      { regex: /\s+(LIMIT)\b/gi, replacement: "\n$1 " },
      {
        regex: /\s+(INNER JOIN|LEFT JOIN|RIGHT JOIN|JOIN)\b/gi,
        replacement: "\n  $1 ",
      },
      { regex: /\s+(AS)\s+/gi, replacement: "\n  $1 " },
    ];

    clausePatterns.forEach(({ regex, replacement }) => {
      formatted = formatted.replace(regex, replacement);
    });

    // Collapse multiple blank lines to at most two newlines, then trim each line's leading space
    formatted = formatted.replace(/\n{3,}/g, "\n\n");
    formatted = formatted
      .split("\n")
      .map((line) => line.trimStart())
      .join("\n")
      .trim();

    return formatted;
  };

  const highlightSql = (sql) => {
    if (!sql) return "";

    const formatted = formatSqlForDisplay(sql);
    let escaped = escapeHtml(formatted);

    // Accent keyword(s)
    escaped = escaped.replace(
      /\b(LIMIT)\b/gi,
      (match) => `<span class="sql-keyword-accent">${match.toUpperCase()}</span>`
    );

    // Primary SQL keywords
    escaped = escaped.replace(
      /\b(SELECT|WITH|FROM|WHERE|GROUP BY|ORDER BY|COUNT|AS|AND|OR|INNER JOIN|LEFT JOIN|RIGHT JOIN|JOIN|ON|HAVING|DISTINCT)\b/gi,
      (match) => `<span class="sql-keyword">${match.toUpperCase()}</span>`
    );

    // Replace {Query 1}, {Query 2} etc. with bold heading (no curly braces)
    escaped = escaped.replace(
      /\{Query\s*(\d+)\}/gi,
      (_, num) => `<span class="sql-query-heading">Query ${num}</span>`
    );

    return escaped;
  };

  const handleCopySQL = (sqlQuery, index) => {
    if (!sqlQuery) return;
    navigator.clipboard
      .writeText(sqlQuery)
      .then(() => {
        setCopiedSqlIndex(index);
        setTimeout(() => setCopiedSqlIndex(null), 2000);
      })
      .catch(() => {});
  };

  const handleExportTableCSV = async ({ msgIndex = null, exportType, messageId, sessionId }) => {
    if (!messageId || !sessionId || !exportType) return;
    const key = `${msgIndex ?? "fullview"}:${exportType}`;
    setExportCsvLoadingKey(key);

    try {
      const res = await chatService.exportQueryResults({
        message_id: messageId,
        session_id: sessionId,
        export_type: exportType,
      });

      const headers = res?.headers || [];
      const data = res?.data || [];
      if (!Array.isArray(headers) || headers.length === 0) return;

      downloadCsv({
        headers,
        data,
        filename: `table-export-${sessionId}-${messageId}-${exportType}-${Date.now()}.csv`,
      });
    } catch (e) {
      console.error("CSV export failed:", e);
    } finally {
      setExportCsvLoadingKey(null);
      setOpenTableExportDropdown(null);
    }
  };

  const buildTableSortProps = (
    contextKey,
    msgIndex,
    messageId,
    sessionId,
    { queryRowCount, loadedCount } = {}
  ) => {
    const openColumn = (() => {
      if (!openTableSortDropdown) return null;
      const prefix = `${contextKey}:`;
      if (String(openTableSortDropdown).startsWith(prefix)) {
        return String(openTableSortDropdown).slice(prefix.length);
      }
      return null;
    })();

    const activeSort =
      msgIndex != null && !Number.isNaN(msgIndex) ? tableActiveSortByMsg[msgIndex] : null;

    const totalRowCount = resolveSortRowCount({ queryRowCount, loadedCount });

    return {
      messageId,
      sessionId,
      queryRowCount,
      loadedCount,
      totalRowCount,
      openDropdownKey: openColumn,
      onToggleSortDropdown: (col) => {
        const key = `${contextKey}:${col}`;
        setOpenTableSortDropdown(openTableSortDropdown === key ? null : key);
      },
      onApplySort: ({ column_name, column_key, sort_order, limit }) =>
        handleAdminTableSort({
          msgIndex,
          messageId,
          sessionId,
          column_name,
          column_key,
          sort_order,
          limit,
        }),
      isSortLoading: (col) => tableSortLoadingKey === `${contextKey}:${col}`,
      activeSort: activeSort
        ? { columnKey: activeSort.columnKey, sortOrder: activeSort.sortOrder }
        : null,
    };
  };

  const handleAdminTableSort = async ({
    msgIndex,
    messageId,
    sessionId,
    column_name,
    column_key,
    sort_order,
    limit,
  }) => {
    if (!messageId || !sessionId || !column_name || !sort_order) return;

    const contextKey = msgIndex ?? "fullview";
    const loadingKey = column_key ?? column_name;
    setTableSortLoadingKey(`${contextKey}:${loadingKey}`);

    try {
      const resolvedMsgIndex =
        msgIndex != null && !Number.isNaN(msgIndex)
          ? msgIndex
          : parseTableMessageIndex(fullViewTable?.tableKey);

      if (resolvedMsgIndex == null || Number.isNaN(resolvedMsgIndex)) return;

      const tableKey = `table-${resolvedMsgIndex}`;
      const msg = sessionMessagesRef.current[resolvedMsgIndex];
      const referenceRows = msg?.rawResults;
      const baseSql = msg?.sqlQuery;
      if (!baseSql) return;

      const pag = tablePaginationRef.current[tableKey];
      const { totalCount: sortTotalCount } = resolveTableFooterState({
        rawResults: referenceRows,
        dataSummary: msg?.dataSummary ?? {},
        paginationInfo: msg?.pagination ?? {},
        storedTotalCount: msg?.totalCount ?? pag?.totalCount,
        rawResultsBuffer: msg?.rawResultsBuffer,
      });

      const { response: res, sortedSql: builtSortedSql } = await fetchSortedTableViaExecuteSql({
        baseSql,
        columnName: column_name,
        sortOrder: sort_order,
        userLimit: limit ?? null,
        knownTotalCount: sortTotalCount,
        knownLoadedCount: referenceRows?.length ?? 0,
        sessionId,
        referenceRows,
      });

      if (!res?.success && !(res?.data?.length > 0)) return;

      const sortApplied = applyTableSortResult({
        response: res,
        msgIndex: resolvedMsgIndex,
        tableKey,
        referenceRows,
        fullViewReferenceRows:
          fullViewTable?.tableKey === tableKey ? fullViewTable.data : null,
        setRows: setSessionMessages,
        setTablePagination,
        setFullViewTable,
      });

      setTableActiveSortByMsg((prev) => ({
        ...prev,
        [resolvedMsgIndex]: {
          columnKey: loadingKey,
          columnName: column_name,
          sortOrder: sort_order,
          limit: limit ?? null,
          sortedSql:
            sortApplied?.sortedSql ??
            builtSortedSql ??
            res.sorted_sql ??
            res.sortedSql ??
            null,
        },
      }));
    } catch (e) {
      console.error("Table sort failed:", e);
      toast.error("Failed to sort table. Please try again.");
    } finally {
      setTableSortLoadingKey(null);
      setOpenTableSortDropdown(null);
    }
  };

  const handleAdminTablePagination = async (msgIndex, direction, sqlQueryFromProp) => {
    const msg = sessionMessagesRef.current[msgIndex];
    const sqlQuery = sqlQueryFromProp || msg?.sqlQuery;
    if (direction === "prev" || !sqlQuery || !selectedSessionId) return;

    const tableKey = `table-${msgIndex}`;
    if (loadingPaginationRef.current[tableKey]) return;

    const pag = tablePaginationRef.current[tableKey];
    const existingRows = msg?.rawResults || [];
    const loadedCount = existingRows.length;
    const requestOffset = resolveTableLoadOffset(pag, loadedCount);
    const { totalCount, hasMore: initialHasMore } = resolveTableFooterState({
      rawResults: existingRows,
      dataSummary: msg?.dataSummary ?? {},
      paginationInfo: msg?.pagination ?? {},
      storedTotalCount: msg?.totalCount ?? pag?.totalCount,
      rawResultsBuffer: msg?.rawResultsBuffer,
    });
    const limit = getLoadMoreBatchSize(loadedCount, totalCount, TABLE_PAGE_SIZE);

    if (limit <= 0 || initialHasMore === false) {
      setTablePagination((prev) => ({
        ...prev,
        [tableKey]: { ...prev[tableKey], hasMore: false },
      }));
      return;
    }

    setLoadingPagination((prev) => ({ ...prev, [tableKey]: true }));

    const applyLoadMoreResult = (result, paginationInfo = msg?.pagination ?? {}) => {
      const { totalCount: resolvedTotalCount, hasMore: resolvedHasMore } =
        resolveTableFooterState({
          rawResults: result.merged,
          dataSummary: msg?.dataSummary ?? {},
          paginationInfo,
          storedTotalCount: result.totalCount ?? totalCount,
          rawResultsBuffer: msg?.rawResultsBuffer,
        });

      if (result.appendedCount === 0 && result.merged.length === existingRows.length) {
        setTablePagination((prev) => ({
          ...prev,
          [tableKey]: {
            ...prev[tableKey],
            hasMore: resolvedHasMore,
            offset: result.nextOffset,
            loadedCount: result.loadedCount,
            totalCount: resolvedTotalCount,
          },
        }));
        return;
      }

      setTablePagination((prev) => ({
        ...prev,
        [tableKey]: {
          offset: result.nextOffset,
          loadedCount: result.loadedCount,
          totalCount: resolvedTotalCount,
          hasMore: resolvedHasMore,
        },
      }));

      setSessionMessages((prev) => {
        if (!prev[msgIndex]) return prev;
        const updated = [...prev];
        updated[msgIndex] = {
          ...updated[msgIndex],
          rawResults: result.merged,
          totalCount: resolvedTotalCount ?? updated[msgIndex].totalCount,
          pagination: paginationInfo,
        };
        return updated;
      });

      setFullViewTable((fv) =>
        fv?.tableKey === tableKey
          ? {
              ...fv,
              data: result.merged,
              totalCount: resolvedTotalCount,
              hasMore: resolvedHasMore,
            }
          : fv
      );
    };

    try {
      const buffered = applyBufferedTableLoadMore({
        existingRows,
        rawResultsBuffer: msg?.rawResultsBuffer,
        limit,
        previousTotalCount: totalCount,
        paginationInfo: msg?.pagination ?? {},
      });
      if (buffered) {
        applyLoadMoreResult(buffered, msg?.pagination ?? {});
        return;
      }

      const activeSort = tableActiveSortByMsg[msgIndex];
      const resolvedSql = resolveExecuteSqlQuery(sqlQuery, activeSort, {
        offset: requestOffset,
        limit,
      });
      const sqlForExecute = resolvedSql?.sql ?? sqlQuery;
      const executeOffset = resolvedSql?.offset ?? requestOffset;
      const executeLimit = resolvedSql?.limit ?? limit;

      const response = await chatService.executeSql(
        sqlForExecute,
        executeOffset,
        executeLimit,
        selectedSessionId,
        getUserId()
      );

      const { rows: newData, pagination: paginationInfo, dataSummary, ok } =
        parseExecuteSqlResponse(response, existingRows);

      if (!ok && (!newData || newData.length === 0)) {
        const { hasMore: stillHasMore } = resolveTableFooterState({
          rawResults: existingRows,
          dataSummary: msg?.dataSummary ?? {},
          paginationInfo,
          storedTotalCount: totalCount,
          rawResultsBuffer: msg?.rawResultsBuffer,
        });
        setTablePagination((prev) => ({
          ...prev,
          [tableKey]: { ...prev[tableKey], hasMore: stillHasMore },
        }));
        return;
      }

      const result = processLoadMoreResponse({
        existingRows,
        newRows: newData || [],
        requestOffset,
        limit,
        paginationInfo,
        dataSummary,
        previousTotalCount: totalCount,
        bufferLength: msg?.rawResultsBuffer?.length ?? 0,
      });

      applyLoadMoreResult(result, paginationInfo);
    } catch (error) {
      console.error("Admin load more table rows failed:", error);
    } finally {
      setLoadingPagination((prev) => ({ ...prev, [tableKey]: false }));
    }
  };

  const handleAdminFullViewTableLoadMore = async () => {
    if (!fullViewTable?.sqlQuery || !selectedSessionId || fullViewTableLoading) return;

    const tableKey = fullViewTable.tableKey;
    const existingRows = fullViewTable.data ?? [];
    const pag = tableKey ? tablePaginationRef.current[tableKey] : null;
    const msgIndex =
      tableKey != null ? Number(String(tableKey).replace("table-", "")) : NaN;
    const msg = Number.isNaN(msgIndex) ? null : sessionMessagesRef.current[msgIndex];
    const loadedCount = existingRows.length;
    const requestOffset = resolveTableLoadOffset(pag, loadedCount);
    const { totalCount: resolvedTotal, hasMore: initialHasMore } = resolveTableFooterState({
      rawResults: existingRows,
      dataSummary: msg?.dataSummary ?? {},
      paginationInfo: msg?.pagination ?? {},
      storedTotalCount: fullViewTable.totalCount ?? msg?.totalCount,
      rawResultsBuffer: msg?.rawResultsBuffer,
    });
    const limit = getLoadMoreBatchSize(loadedCount, resolvedTotal, TABLE_PAGE_SIZE);

    if (limit <= 0 || initialHasMore === false) {
      setFullViewTable((prev) => (prev ? { ...prev, hasMore: false } : prev));
      return;
    }

    setFullViewTableLoading(true);
    try {
      const buffered = applyBufferedTableLoadMore({
        existingRows,
        rawResultsBuffer: msg?.rawResultsBuffer,
        limit,
        previousTotalCount: resolvedTotal,
      });
      if (buffered) {
        const footer = resolveTableFooterState({
          rawResults: buffered.merged,
          dataSummary: msg?.dataSummary ?? {},
          paginationInfo: msg?.pagination ?? {},
          storedTotalCount: buffered.totalCount ?? resolvedTotal,
          rawResultsBuffer: msg?.rawResultsBuffer,
        });
        setFullViewTable((prev) =>
          prev
            ? {
                ...prev,
                data: buffered.merged,
                hasMore: footer.hasMore,
                totalCount: footer.totalCount,
              }
            : prev
        );
        if (tableKey) {
          setTablePagination((prev) => ({
            ...prev,
            [tableKey]: {
              offset: buffered.nextOffset,
              loadedCount: buffered.loadedCount,
              totalCount: footer.totalCount,
              hasMore: footer.hasMore,
            },
          }));
          if (!Number.isNaN(msgIndex)) {
            setSessionMessages((prev) => {
              if (!prev[msgIndex]) return prev;
              const updated = [...prev];
              updated[msgIndex] = {
                ...updated[msgIndex],
                rawResults: buffered.merged,
                totalCount: footer.totalCount ?? updated[msgIndex].totalCount,
              };
              return updated;
            });
          }
        }
        return;
      }

      const activeSort = !Number.isNaN(msgIndex) ? tableActiveSortByMsg[msgIndex] : null;
      const baseSql = msg?.sqlQuery ?? fullViewTable.sqlQuery;
      const resolvedSql = resolveExecuteSqlQuery(baseSql, activeSort, {
        offset: requestOffset,
        limit,
      });
      const sqlForExecute = resolvedSql?.sql ?? baseSql;
      const executeOffset = resolvedSql?.offset ?? requestOffset;
      const executeLimit = resolvedSql?.limit ?? limit;

      const response = await chatService.executeSql(
        sqlForExecute,
        executeOffset,
        executeLimit,
        selectedSessionId,
        getUserId()
      );
      const { rows: newRows, pagination, dataSummary, ok } = parseExecuteSqlResponse(
        response,
        existingRows
      );
      if (!ok && (!newRows || newRows.length === 0)) return;

      const result = processLoadMoreResponse({
        existingRows,
        newRows: newRows || [],
        requestOffset,
        limit,
        paginationInfo: pagination,
        dataSummary,
        previousTotalCount: resolvedTotal,
        bufferLength: msg?.rawResultsBuffer?.length ?? 0,
      });

      const footer = resolveTableFooterState({
        rawResults: result.merged,
        dataSummary: msg?.dataSummary ?? dataSummary ?? {},
        paginationInfo: pagination,
        storedTotalCount: result.totalCount ?? resolvedTotal,
        rawResultsBuffer: msg?.rawResultsBuffer,
      });

      setFullViewTable((prev) =>
        prev
          ? {
              ...prev,
              data: result.merged,
              hasMore: footer.hasMore,
              totalCount: footer.totalCount,
            }
          : prev
      );

      if (tableKey) {
        setTablePagination((prev) => ({
          ...prev,
          [tableKey]: {
            offset: result.nextOffset,
            loadedCount: result.loadedCount,
            totalCount: footer.totalCount,
            hasMore: footer.hasMore,
          },
        }));

        const msgIndex = parseTableMessageIndex(tableKey);
        if (!Number.isNaN(msgIndex)) {
          setSessionMessages((prev) => {
            if (!prev[msgIndex]) return prev;
            const updated = [...prev];
            updated[msgIndex] = {
              ...updated[msgIndex],
              rawResults: result.merged,
              totalCount: footer.totalCount ?? updated[msgIndex].totalCount,
            };
            return updated;
          });
        }
      }
    } catch (error) {
      console.error("Admin full view load more failed:", error);
    } finally {
      setFullViewTableLoading(false);
    }
  };

  const stripAdminMessageBodyText = (body) =>
    String(body ?? "")
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<[^>]+>/g, "")
      .trim();

  const isAdminReasoningMessage = (msg) =>
    Boolean(msg?.isReasoning || String(msg?.queryType || "").toLowerCase() === "reasoning");

  const getUserQueryForAdminMessage = (msgIndex) => {
    const conv = sessionMessagesRef.current;
    for (let i = msgIndex - 1; i >= 0; i--) {
      if (conv[i]?.role === "user") {
        return stripAdminMessageBodyText(conv[i].content);
      }
    }
    return "";
  };

  const canRequestAdminMessageCharts = (msg) =>
    Boolean(
      msg?.role === "assistant" &&
      !isAdminReasoningMessage(msg) &&
      msg?.rawResults?.length > 0 &&
      msg?.sqlQuery &&
      msg?.message_id &&
      !msg?.chartsVizFailed &&
      !hasDisplayableCharts(msg?.charts)
    );

  const shouldAutoFetchAdminMessageCharts = (msg) =>
    Boolean(canRequestAdminMessageCharts(msg) && !msg?.chartsVizChecked);

  const buildAdminDisplayableChartsFromSuggestions = (chartSuggestions, rawResults) => {
    if (!Array.isArray(chartSuggestions) || chartSuggestions.length === 0) return [];

    const newCharts = chartSuggestions
      .map((chart) => {
        const transformed = transformChartData(chart);
        if (!transformed) return null;
        return { ...transformed, ref: React.createRef() };
      })
      .filter(Boolean);

    return filterDisplayableCharts(
      enrichChartsFromRawResults(rawResults, newCharts)
    ).map((chart) => ({
      ...chart,
      ref: chart.ref ?? React.createRef(),
      title:
        chart.title ||
        `${humanizeColumnLabel(chart.y_column)} by ${humanizeColumnLabel(chart.x_column)}`,
    }));
  };

  const fetchChartsForAdminMessage = async (message, userQuery) => {
    if (!message?.sqlQuery || !message?.rawResults?.length || !message?.message_id) {
      return [];
    }

    const numericResults = prepareVisualizationData(message.rawResults);
    const visualizationResponse = await chatService.getVisualization(
      userQuery,
      message.sqlQuery,
      numericResults,
      selectedSessionId,
      message.message_id,
      selectedUserId || getUserId()
    );

    const vizPayload =
      visualizationResponse?.visualization_data ||
      visualizationResponse?.metadata?.visualization_data ||
      visualizationResponse;
    const suggestedCharts = resolveSuggestedChartsFromMessage(
      visualizationResponse,
      vizPayload
    );

    if (!suggestedCharts) {
      return [];
    }

    return buildAdminDisplayableChartsFromSuggestions(
      suggestedCharts,
      message.rawResults
    );
  };

  const prefetchVisualizationForAdminMessage = async (msgIndex, messageSnapshot = null) => {
    const message = messageSnapshot ?? sessionMessagesRef.current[msgIndex];
    if (!message || !shouldAutoFetchAdminMessageCharts(message)) return;

    const flightKey = String(message.message_id ?? msgIndex);
    if (chartVizPrefetchInFlightRef.current.has(flightKey)) return;
    chartVizPrefetchInFlightRef.current.add(flightKey);

    try {
      const displayableCharts = await fetchChartsForAdminMessage(
        message,
        getUserQueryForAdminMessage(msgIndex)
      );
      const hasCharts = displayableCharts.length > 0;

      setSessionMessages((prev) =>
        prev.map((m, i) =>
          i === msgIndex
            ? {
                ...m,
                charts: displayableCharts,
                hasSuggestedCharts: hasCharts,
                chartsVizFailed: !hasCharts,
                chartsVizChecked: true,
              }
            : m
        )
      );
    } catch {
      setSessionMessages((prev) =>
        prev.map((m, i) =>
          i === msgIndex
            ? {
                ...m,
                chartsVizFailed: true,
                chartsVizChecked: true,
                hasSuggestedCharts: false,
              }
            : m
        )
      );
    } finally {
      chartVizPrefetchInFlightRef.current.delete(flightKey);
    }
  };

  useEffect(() => {
    if (!showMessagesModal) return;
    sessionMessages.forEach((msg, index) => {
      if (msg?.role !== "assistant") return;
      if (!shouldAutoFetchAdminMessageCharts(msg)) return;
      prefetchVisualizationForAdminMessage(index, msg);
    });
  }, [sessionMessages, showMessagesModal, selectedSessionId]);

  const renderDataTable = (rawResults, msgIndex, message) => {
    if (!rawResults || rawResults.length === 0) return null;
    const columns = getTableColumnSchema(rawResults);
    const messageId = message?.message_id;
    const sqlQuery = message?.sqlQuery;
    const tableKey = `table-${msgIndex}`;
    const pag = tablePagination[tableKey];
    const isLoading = loadingPagination[tableKey] || false;
    const { loadedCount, totalCount, hasMore, loadMoreCount } = resolveTableFooterState({
      rawResults,
      dataSummary: message?.dataSummary ?? {},
      paginationInfo: message?.pagination ?? {},
      storedTotalCount: message?.totalCount ?? pag?.totalCount,
      rawResultsBuffer: message?.rawResultsBuffer,
    });
    const hasCharts = hasDisplayableCharts(message.charts);
    const showChartSwitch = Boolean(message?.hasSuggestedCharts && hasCharts);
    const chartsReady = hasCharts;
    const viewMode =
      showChartSwitch && (tableResultViewModes[msgIndex] ?? "table") === "chart"
        ? "chart"
        : "table";

    const handleViewModeChange = (mode) => {
      if (mode === "table") {
        setTableResultViewModes((prev) => ({ ...prev, [msgIndex]: "table" }));
        return;
      }
      if (!showChartSwitch || !chartsReady) return;
      setTableResultViewModes((prev) => ({ ...prev, [msgIndex]: "chart" }));
    };

    const tableEl = (
      <SqlResultDataTable
        variant="admin"
        embedInPanel
        rawResults={rawResults}
        formatScalarForDisplay={safeText}
        admin={{
          totalCount,
          loadedCount,
          hasMore,
          isLoadingMore: isLoading,
          loadMoreCount,
          sqlQuery,
          onLoadMore: () => handleAdminTablePagination(msgIndex, "next", sqlQuery),
          onOpenFullView: () =>
            setFullViewTable({
              data: rawResults,
              columns,
              messageId,
              sessionId: selectedSessionId,
              totalCount,
              tableKey,
              sqlQuery,
              hasMore,
            }),
          export: {
            messageId,
            getCsvExportOptions,
            onPickExportOption: (exportType) =>
              handleExportTableCSV({
                msgIndex,
                exportType,
                messageId,
                sessionId: selectedSessionId,
              }),
            isOpen: openTableExportDropdown === msgIndex,
            onToggle: () => {
              setOpenTableExportDropdown(openTableExportDropdown === msgIndex ? null : msgIndex);
            },
            isExportOptionLoading: (exportType) =>
              exportCsvLoadingKey === `${msgIndex}:${exportType}`,
            menuPlacement: "above",
          },
          sort: buildTableSortProps(msgIndex, msgIndex, messageId, selectedSessionId, {
            queryRowCount: totalCount,
            loadedCount,
          }),
        }}
      />
    );

    const chartEl = chartsReady ? renderAdminMessageChartsPanel(message, msgIndex) : null;

    return (
      <SqlResultViewSwitcher
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
        showChartSwitch={showChartSwitch}
        tableContent={tableEl}
        chartContent={chartEl}
      />
    );
  };

  // Get user initials for avatar
  const getUserInitials = (name) => {
    if (!name) return "U";
    const parts = name.split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name[0].toUpperCase();
  };

  const mapSessionMessage = (msg) => {
    const vizData =
      msg.metadata?.visualization_data || msg.visualization_data;
    const rawResults =
      vizData?.raw_results || msg.raw_results || msg.data || [];
    const suggestedCharts = resolveSuggestedChartsFromMessage(msg, vizData);

    let charts = [];
    if (suggestedCharts) {
      let filteredCharts = suggestedCharts;

      if (msg.metadata?.intent === "reasoning_workflow") {
        filteredCharts = suggestedCharts.filter(
          (chart) => chart.type === "line" || chart.chart_type === "line"
        );
      }

      charts = filterDisplayableCharts(
        enrichChartsFromRawResults(rawResults, filteredCharts)
      ).map((chart) => ({
        ...chart,
        ref: React.createRef(),
        title:
          chart.title ||
          `${humanizeColumnLabel(chart.y_column)} by ${humanizeColumnLabel(chart.x_column)}`,
      }));
    }

    const sqlQuery =
      msg.metadata?.visualization_data?.sql ||
      msg.metadata?.sql_query ||
      msg.sql_query ||
      vizData?.sql;
    const showSqlQuery =
      sqlQuery && sqlQuery !== "General route - no SQL needed";

    let messageBody = msg.content || msg.message || "";
    if (msg.role !== "user") {
      const answer =
        vizData?.answer || msg.metadata?.visualization_data?.answer;
      if (answer) {
        messageBody = answer;
      }
    }

    let cleanedBody = messageBody;
    if (rawResults && rawResults.length > 0) {
      cleanedBody = messageBody
        .replace(/\|[^\n]+\|[\s\S]*?(?=\n\n|\*\*|$)/g, "")
        .trim();
    }
    const { pagination, dataSummary } = extractTablePaginationSources({
      metadata: msg.metadata ?? {},
      visualization_data: vizData ?? msg.visualization_data,
    });
    const isReasoningWorkflow = msg.metadata?.intent === "reasoning_workflow";
    const tableMeta = isReasoningWorkflow
      ? null
      : buildTableMetaFromSource({
          rawResults,
          pagination,
          dataSummary,
        });

    return {
      message_id: msg.message_id,
      role: msg.role,
      content: cleanedBody || "",
      timestamp: msg.timestamp || msg.created_at,
      feedback: msg.feedback || null,
      comment: msg.comment || msg.feedback_comment || null,
      charts,
      rawResults: tableMeta?.displayedResults ?? null,
      rawResultsBuffer: tableMeta?.rawResultsBuffer ?? null,
      totalCount: tableMeta?.totalCount ?? null,
      pagination: tableMeta?.pagination ?? pagination,
      dataSummary: tableMeta?.dataSummary ?? dataSummary,
      sqlQuery: showSqlQuery ? sqlQuery : null,
      queryType:
        msg.metadata?.query_type ||
        msg.query_type ||
        (isReasoningWorkflow ? "reasoning" : "qa"),
      isReasoning: isReasoningWorkflow,
      subQueries: msg.metadata?.sub_queries || msg.sub_queries || null,
      hasSuggestedCharts: charts.length > 0,
      chartsVizChecked: charts.length > 0,
    };
  };

  const renderAdminMessageChartsPanel = (message, index) => {
    if (!hasDisplayableCharts(message.charts)) return null;

    return (
      <div className="charts-wrapper charts-wrapper-flex">
        {message.charts.map((chart, cIdx) => {
          const chartTypeRaw = String(chart.type || chart.chart_type || "").toLowerCase();
          const validData = Array.isArray(chart.data)
            ? chart.data.filter((d) => isChartPointValid(d, chart, chartTypeRaw))
            : [];
          if (validData.length === 0) return null;

          const chartKey = `msg-${index}-${cIdx}`;
          const chartType = resolveChatChartType(
            chart,
            msgChartTypeOverrides[chartKey],
            validData.length
          );

          return (
            <div key={cIdx} className="chart-item-wrapper-styled" ref={chart.ref}>
              <div className="chart-header">
                <h3 className="chart-title">{chart.title || "Chart"}</h3>
                <div className="chart-header-actions">
                  <div className="chart-type-dropdown-wrapper" style={{ position: "relative" }}>
                    <button
                      type="button"
                      className="chart-action-btn chart-type-toggle-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenMsgChartTypeDropdown((prev) =>
                          prev === chartKey ? null : chartKey
                        );
                      }}
                      title="Change chart type"
                    >
                      {chartType === "pie" && <IconChartPie width={14} height={14} />}
                      {chartType === "bar" && <IconChartBar width={14} height={14} />}
                      {chartType === "line" && <IconChartLine width={14} height={14} />}
                      <span style={{ textTransform: "capitalize" }}>{chartType}</span>
                      <IconChevronDownBold width={10} height={10} />
                    </button>
                    {openMsgChartTypeDropdown === chartKey && (
                      <div
                        className="chart-type-dropdown-menu"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {getChartTypeOptionsForPointCount(validData.length).map(
                          ({ type: t, label }) => {
                            const icon =
                              t === "pie" ? (
                                <IconChartPie width={14} height={14} />
                              ) : t === "line" ? (
                                <IconChartLine width={14} height={14} />
                              ) : (
                                <IconChartBar width={14} height={14} />
                              );
                            return (
                              <button
                                key={t}
                                type="button"
                                className={`chart-type-dropdown-item${chartType === t ? " active" : ""}`}
                                onClick={() => {
                                  setMsgChartTypeOverrides((prev) => ({ ...prev, [chartKey]: t }));
                                  setOpenMsgChartTypeDropdown(null);
                                }}
                              >
                                {icon}
                                {label}
                              </button>
                            );
                          }
                        )}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setFullViewMessageChart({
                        ...chart,
                        data: validData,
                        _selectedType: chartType,
                        _chartKey: chartKey,
                      })
                    }
                    className="chart-action-btn"
                  >
                    <IconExternalExpand width={14} height={14} />
                    Full View
                  </button>
                </div>
              </div>
              <EmcureAdminInlineChartPlot
                chartType={chartType}
                validData={validData}
                chart={chart}
                cIdx={cIdx}
                formatDateAxis={formatDateValue}
                parseTooltipNumber={parseTooltipMetricValue}
                formatCategoryLabel={formatPieCategoryLabel}
              />
              <ChartPdfExportTable chart={{ ...chart, data: validData }} />
            </div>
          );
        })}
      </div>
    );
  };

  // Handle view messages
  const handleViewMessages = async (sessionId, userId, preloadedMessages) => {
    setSelectedSessionId(sessionId);
    setSelectedUserId(userId);
    setLoadingMessages(true);
    setShowMessagesModal(true);

    setMsgChartTypeOverrides({});
    setOpenMsgChartTypeDropdown(null);
    setShowSubQueryDropdown({});
    setTableResultViewModes({});
    chartVizPrefetchInFlightRef.current.clear();

    if (preloadedMessages && Array.isArray(preloadedMessages) && preloadedMessages.length > 0) {
      const sorted = [...preloadedMessages].sort(
        (a, b) => (a.message_index ?? 0) - (b.message_index ?? 0)
      );
      const mapped = sorted.map(mapSessionMessage);
      sessionMessagesRef.current = mapped;
      setSessionMessages(mapped);
      setTablePagination(buildInitialTablePagination(mapped));
      setLoadingMessages(false);
      return;
    }

    try {
      // Use the new API endpoint: /api/sessions/{sessionId}/messages
      const response = await adminService.getSessionMessages(sessionId);

      // Transform the response to match the expected format
      if (response && response.messages && Array.isArray(response.messages)) {
        const transformedMessages = response.messages.map(mapSessionMessage);
        sessionMessagesRef.current = transformedMessages;
        setSessionMessages(transformedMessages);
        setTablePagination(buildInitialTablePagination(transformedMessages));
      } else if (Array.isArray(response)) {
        // Fallback for array response
        const transformedMessages = response.map(mapSessionMessage);
        sessionMessagesRef.current = transformedMessages;
        setSessionMessages(transformedMessages);
        setTablePagination(buildInitialTablePagination(transformedMessages));
      } else {
        setSessionMessages([]);
        setTablePagination({});
      }
    } catch (error) {
      setSessionMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  };

  const closeMessagesModal = () => {
    setShowMessagesModal(false);
    setSelectedSessionId(null);
    setSelectedUserId(null);
    setSessionMessages([]);
    setMessageSearchQuery("");
    setFullViewMessageChart(null);
    setFullViewTable(null);
    setMsgChartTypeOverrides({});
    setOpenMsgChartTypeDropdown(null);
    setTablePagination({});
    setLoadingPagination({});
    setOpenTableExportDropdown(null);
    setShowSubQueryDropdown({});
    setTableResultViewModes({});
    chartVizPrefetchInFlightRef.current.clear();
  };

  // Handle view feedback message
  const handleViewFeedbackMessage = (feedback) => {
    setSelectedFeedbackMessage(feedback);
    setShowFeedbackMessageModal(true);
  };

  const closeFeedbackMessageModal = () => {
    setShowFeedbackMessageModal(false);
    setSelectedFeedbackMessage(null);
  };

  /** Read-only single thumb — up for positive, down for negative */
  const renderFeedbackThumbs = (feedback) => {
    const isUp = feedback === 1;
    const isDown = feedback === -1;
    if (!isUp && !isDown) {
      return <span className="admin-feedback-none">—</span>;
    }
    return (
      <div
        className="message-feedback admin-feedback-thumbs-readonly admin-feedback-thumbs-single"
        aria-label={isUp ? "Positive feedback" : "Negative feedback"}
      >
        <span
          className="feedback-btn active"
          title={isUp ? "Positive" : "Negative"}
        >
          {isUp ? (
            <IconThumbUpFilled width={16} height={16} />
          ) : (
            <IconThumbDownFilled width={16} height={16} />
          )}
        </span>
      </div>
    );
  };

  // Filter messages based on search query
  const getFilteredMessages = () => {
    if (!messageSearchQuery.trim()) {
      return sessionMessages;
    }
    const query = messageSearchQuery.toLowerCase();
    return sessionMessages.filter((message) =>
      message.content.toLowerCase().includes(query)
    );
  };

  // Handle PDF download — message text/tables plus chart cards (title + plot layout).
  const handleDownloadPDF = async () => {
    const messagesToExport = getFilteredMessages();
    if (messagesToExport.length === 0) {
      return;
    }

    try {
      const pdf = new jsPDF();
      const pageHeight = pdf.internal.pageSize.height;
      const margin = 20;
      const maxWidth = 170;
      const maxChartWidth = maxWidth;
      const maxChartHeight = 220;

      let yPosition = 20;

      pdf.setFontSize(18);
      pdf.setTextColor(30, 41, 59);
      pdf.text("Emcure AI Assistant - Chat History", margin, yPosition);
      yPosition += 10;

      pdf.setFontSize(10);
      pdf.setTextColor(100, 100, 100);
      pdf.text(`Session ID: ${selectedSessionId}`, margin, yPosition);
      yPosition += 6;
      pdf.text(`User ID: ${selectedUserId}`, margin, yPosition);
      yPosition += 6;
      pdf.text(`Generated: ${new Date().toLocaleString()}`, margin, yPosition);
      yPosition += 15;

      for (let idx = 0; idx < messagesToExport.length; idx++) {
        const message = messagesToExport[idx];

        if (yPosition > pageHeight - 40) {
          pdf.addPage();
          yPosition = 20;
        }

        pdf.setFontSize(11);
        pdf.setTextColor(30, 64, 175);
        const sender =
          message.role === "user" ? selectedUserId || "User" : "AI Assistant";
        pdf.text(`${sender} - ${formatTime(message.timestamp)}`, margin, yPosition);
        yPosition += 7;

        pdf.setFontSize(10);
        pdf.setTextColor(0, 0, 0);
        let bodyText = message.content || "";
        if (message.rawResults?.length > 0) {
          bodyText = bodyText
            .replace(/\|[^\n]+\|[\s\S]*?(?=\n\n|$)/g, "")
            .trim();
        }
        if (bodyText) {
          const lines = pdf.splitTextToSize(bodyText, maxWidth);
          lines.forEach((line) => {
            if (yPosition > pageHeight - 20) {
              pdf.addPage();
              yPosition = 20;
            }
            pdf.text(line, margin, yPosition);
            yPosition += 5;
          });
        }

        if (message.rawResults?.length > 0) {
          yPosition += 5;
          if (yPosition > pageHeight - 60) {
            pdf.addPage();
            yPosition = 20;
          }

          pdf.setFontSize(9);
          pdf.setTextColor(30, 41, 59);
          pdf.text("Data Table:", margin, yPosition);
          yPosition += 5;

          const columns = Object.keys(message.rawResults[0] || {});
          const columnHeaders = columns.map((col) =>
            col.replace(/_/g, " ").toUpperCase()
          );
          const tableData = message.rawResults.map((row) =>
            columns.map((col) => {
              const value = row[col];
              if (value === null || value === undefined) return "";
              if (typeof value === "number") {
                return Number.isInteger(value)
                  ? value.toLocaleString("en-IN")
                  : value.toFixed(2);
              }
              return String(value);
            })
          );

          try {
            autoTable(pdf, {
              startY: yPosition,
              head: [columnHeaders],
              body: tableData,
              margin: { left: margin, right: margin },
              styles: {
                fontSize: 7,
                cellPadding: 2,
                lineColor: [220, 220, 220],
                lineWidth: 0.5,
                overflow: "linebreak",
                cellWidth: "auto",
              },
              headStyles: {
                fillColor: [30, 41, 59],
                textColor: [255, 255, 255],
                fontStyle: "bold",
                halign: "center",
                fontSize: 8,
                cellPadding: 3,
              },
              bodyStyles: { textColor: [50, 50, 50] },
              alternateRowStyles: { fillColor: [245, 247, 250] },
              columnStyles: columns.reduce((acc, col, colIdx) => {
                const isNumeric = message.rawResults.some(
                  (row) => typeof row[col] === "number"
                );
                acc[colIdx] = isNumeric
                  ? { halign: "right", fontStyle: "bold", textColor: [30, 64, 175] }
                  : { halign: "left" };
                return acc;
              }, {}),
              theme: "grid",
              tableWidth: "auto",
            });
            yPosition = (pdf.lastAutoTable?.finalY || yPosition) + 10;
          } catch {
            pdf.setFontSize(8);
            pdf.text(
              `Table with ${message.rawResults.length} rows`,
              margin,
              yPosition
            );
            yPosition += 8;
          }
        }

        if (hasDisplayableCharts(message.charts)) {
          for (const chart of message.charts) {
            if (!chart.ref?.current) continue;
            try {
              yPosition += 5;
              if (yPosition > pageHeight - maxChartHeight - 20) {
                pdf.addPage();
                yPosition = 20;
              }

              const chartImage = await captureChartCardImage(chart.ref.current);
              if (!chartImage) continue;

              const img = new Image();
              img.src = chartImage;
              await new Promise((resolve) => {
                img.onload = resolve;
                img.onerror = resolve;
              });

              const { width: imgW, height: imgH } = fitChartImageToPdf(
                img.width || 1,
                img.height || 1,
                maxChartWidth,
                maxChartHeight
              );

              if (yPosition + imgH > pageHeight - 20) {
                pdf.addPage();
                yPosition = 20;
              }

              pdf.addImage(chartImage, "PNG", margin, yPosition, imgW, imgH);
              yPosition += imgH + 10;
            } catch {
              // skip chart if capture fails
            }
          }
        }

        if (message.sqlQuery) {
          yPosition += 3;
          if (yPosition > pageHeight - 30) {
            pdf.addPage();
            yPosition = 20;
          }
          pdf.setFontSize(9);
          pdf.setTextColor(100, 100, 100);
          pdf.text("SQL Query:", margin, yPosition);
          yPosition += 5;
          const sqlLines = pdf.splitTextToSize(message.sqlQuery, maxWidth);
          sqlLines.forEach((line) => {
            if (yPosition > pageHeight - 20) {
              pdf.addPage();
              yPosition = 20;
            }
            pdf.text(line, margin, yPosition);
            yPosition += 4;
          });
        }

        yPosition += 8;
        if (idx < messagesToExport.length - 1) {
          pdf.setDrawColor(200, 200, 200);
          pdf.line(margin, yPosition, margin + maxWidth, yPosition);
          yPosition += 8;
        }
      }

      const fileName = `messages-${selectedSessionId || "session"}-${Date.now()}.pdf`;
      pdf.save(fileName);
    } catch {
      alert("Failed to generate PDF. Please try again.");
    }
  };

  // Handle page change for sessions
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    const today = new Date();
    const todayISO = today.toISOString().slice(0, 10);
    fetchSessions(newPage, selectedEndDate || todayISO, selectedDays, selectedStartTime, selectedEndTime, sessionSearchQuery.trim());
  };

  // Handle session search input change (local search only)
  const handleSessionSearchInput = (query) => {
    setSessionSearchQuery(query);
  };

  // Handle page change for feedbacks
  const handleFeedbackPageChange = (newPage) => {
    setCurrentFeedbackPage(newPage);
    fetchFeedbacks(newPage, selectedDays, selectedStartTime, selectedEndTime, feedbackSearchQuery.trim());
  };

  const handleFeedbackSearchInput = (query) => {
    setFeedbackSearchQuery(query);
  };

  // Sessions and feedbacks are already paginated by the backend
  const paginatedSessions = sessionsData.sessions || [];
  const paginatedFeedbacks = feedbacksData.feedbacks || [];

  const topFeedbacks = [
    "Very helpful and accurate.",
    "Response was slow but detailed.",
    "Needs more clarity on product details.",
    "Great insights on sales analytics.",
    "Helpful summary of top queries.",
  ];

  // Handle click outside to close profile dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setShowProfileDropdown(false);
      }
    };

    if (showProfileDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showProfileDropdown]);

  // Close message chart-type dropdown on click outside (keep open when clicking the toggle/menu)
  useEffect(() => {
    if (!openMsgChartTypeDropdown) return;
    const handler = (e) => {
      if (e.target?.closest?.(".chart-type-dropdown-wrapper")) return;
      setOpenMsgChartTypeDropdown(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [openMsgChartTypeDropdown]);

   const handleLogout = async () => {
     try {
       const msalInstance = getMsalInstance();
       
       tokenRefreshService.stopAutoRefresh();
       
       clearUserInfo();
       
       localStorage.removeItem("appToken");
       localStorage.removeItem("databricksToken");
       localStorage.removeItem("tokenExpiry");
       
       await msalInstance.logoutRedirect({
         postLogoutRedirectUri: "/",
       });
     } catch (error) {
       clearUserInfo();
       localStorage.clear();
       navigate("/");
     }
   };

  // Toggle card expansion to show/hide charts
  // Only one card can be expanded at a time
  const toggleCardExpansion = (cardTitle) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(cardTitle)) {
        // If clicking the same card, close it
        newSet.delete(cardTitle);
      } else {
        // Close all other cards and open the clicked one
        newSet.clear();
        newSet.add(cardTitle);
      }
      return newSet;
    });
  };

  // Centralized chart rendering function
  const renderChart = (card) => {
    // Check if chart data is available and has valid data points
    // Only show "No data available" if there's truly no data (not even zeros)
    const hasValidData = card.chartData && 
                         card.chartData.length > 0 && 
                         card.chartData.some(item => item && item.value !== undefined);
    
    if (!hasValidData) {
      return (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: '100%',
          color: '#9CA3AF',
          fontSize: '13px'
        }}>
          No data available
        </div>
      );
    }

    switch(card.chartType) {
      case "area":
        return (
          <AreaChartComponent
            data={chartDataForDisplay(card.chartData)}
            dataKey="value"
            color={card.color}
          />
        );
      case "bar":
        return (
          <BarChartComponent
            data={card.chartData}
            dataKey="value"
            color={card.color}
          />
        );
      case "line":
        return (
          <LineChartComponent
            data={chartDataForDisplay(card.chartData)}
            dataKey="value"
            color={card.color}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <div className="admin-header-left">
          <div className="admin-logo">
            <img src={EmcureLogo} alt="Emcure logo" />
          </div>
        </div>
        <div className="admin-header-right">
          <DashboardPanelSwitcher variant="admin" />
          <div className={`profile-dropdown-container ${showProfileDropdown ? 'dropdown-open' : ''}`} ref={profileDropdownRef}>
            <button 
              className="profile-dropdown-trigger"
              onClick={() => setShowProfileDropdown(!showProfileDropdown)}
            >
              <img src={iconProfile} alt="Profile" />
              <div className="profile-trigger-info">
                <span className="profile-trigger-name">{getUserName()}</span>
                <span className="profile-trigger-id">{getUserId()}</span>
              </div>
              <IconChevronDown className="profile-dropdown-arrow" width={16} height={16} />
            </button>
            {showProfileDropdown && (
              <div className="profile-dropdown-menu">
                <div className="profile-dropdown-header">
                  <div className="profile-info">
                    <div className="profile-name">{getUserName()}</div>
                    <div className="profile-role">{getUserRole()}</div>
                    <div className="profile-email">{getUserEmail()}</div>
                  </div>
                </div>
                <div className="profile-dropdown-divider"></div>
                <div className="profile-dropdown-items">
                  <button className="profile-dropdown-item" onClick={handleLogout}>
                    <IconLogout width={16} height={16} />
                    <span>Logout</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="admin-main">
        <div className="admin-layout">
          <aside className="admin-sidebar-container">
            <AdminSidebar activeMenu={activeMenu} onMenuChange={setActiveMenu} />
          </aside>
          <section className="admin-content">
            <div className="admin-content-scroll">
        {activeMenu === "dashboard" && (
        <div className="admin-dashboard-view">
        <div className="admin-title-wrapper">
              <div className="admin-title-left">
          <h2 className="admin-title">Dashboard</h2>
          <p className="admin-title-sub">
            High-level snapshot of chatbot usage and engagement
          </p>
        </div>
              <div className="admin-title-right">
                <DateRangePicker {...dateRange} />
              </div>
            </div>
        <div className="main-card admin-dashboard-main-card">
          <section className="quick-actions admin-dashboard-quick-actions">
            {loadingDashboard ? (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                padding: '40px',
                color: '#9CA3AF',
                fontSize: '14px'
              }}>
                Loading dashboard data...
              </div>
            ) : (!loadingDashboard && dashboardStats) ? (
              <>
              <div className="left-column-wrapper">
                {/* First grid - 3 columns */}
                <div className="left-column dashboard-grid">
                  {firstRowCards.map((card) => {
                    // Get icon based on card title
                    const getIcon = (title) => {
                      switch(title) {
                        case "Total Messages":
                        return (
                          <div className="card-icon teal-icon">
                            <AdminCardTotalMessages width={24} height={24} />
                          </div>
                        );
                      case "Total Users":
                        return (
                          <div className="card-icon orange-icon">
                            <AdminCardTotalUsers width={24} height={24} />
                          </div>
                        );
                      case "Cache Hits":
                        return (
                          <div className="card-icon purple-icon">
                            <AdminCardCacheHits width={24} height={24} />
                          </div>
                        );
                      case "Total Feedback":
                        return (
                          <div className="card-icon blue-icon">
                            <AdminCardTotalFeedback width={24} height={24} />
                          </div>
                        );
                      case "Cache Entries":
                        return (
                          <div className="card-icon green-icon">
                            <AdminCardCacheEntries width={24} height={24} />
                          </div>
                        );
                      case "Prompt Cache Hits":
                        return (
                          <div className="card-icon purple-icon">
                            <AdminCardPromptCacheHits width={24} height={24} />
                          </div>
                        );
                      default:
                        return null;
                    }
                  };

                  const renderChart = () => {
                    switch(card.chartType) {
                      case "area":
                        return (
                          <AreaChartComponent
                            data={chartDataForDisplay(card.chartData)}
                            dataKey="value"
                            color={card.color}
                          />
                        );
                      case "bar":
                        return (
                          <BarChartComponent
                            data={card.chartData}
                            dataKey="value"
                            color={card.color}
                          />
                        );
                      case "line":
                        return (
                          <LineChartComponent
                            data={chartDataForDisplay(card.chartData)}
                            dataKey="value"
                            color={card.color}
                          />
                        );
                      default:
                        return null;
                    }
                  };

                  const isExpanded = expandedCards.has(card.title);

                  return (
                    <div 
                      key={card.title} 
                      className={`action-card ${isExpanded ? 'expanded' : ''}`}
                      // onClick={() => toggleCardExpansion(card.title)}
                    >
                      <div className="action-header-with-icon">
                        {getIcon(card.title)}
                        <div className="action-title-wrapper">
                          <div className="action-title">{card.title}</div>
                          <div className="action-desc">{card.desc}</div>
                        </div>
                        {/* <svg 
                          className={`card-expand-icon ${isExpanded ? 'expanded' : ''}`}
                          width="16" 
                          height="16" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        >
                          <polyline points="6 9 12 15 18 9"></polyline>
                        </svg> */}
                      </div>
                      {isExpanded && (
                        <div className="card-chart-container">
                          <ChartContainer>
                            {renderChart()}
                          </ChartContainer>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Second grid - 2 columns */}
              <div className="left-column dashboard-grid-second">
                {secondRowCards.map((card) => {
                  // Get icon based on card title
                  const getIcon = (title) => {
                    switch(title) {
                       case "Cache Hits":
                        return (
                          <div className="card-icon purple-icon">
                            <AdminCardCacheHits width={24} height={24} />
                          </div>
                        );
                      case "Total Feedback":
                        return (
                          <div className="card-icon blue-icon">
                            <AdminCardTotalFeedback width={24} height={24} />
                          </div>
                        );
                      case "Cache Entries":
                        return (
                          <div className="card-icon green-icon">
                            <AdminCardCacheEntries width={24} height={24} />
                          </div>
                        );
                      case "Prompt Cache Hits":
                        return (
                          <div className="card-icon purple-icon">
                            <AdminCardPromptCacheHits width={24} height={24} />
                          </div>
                        );
                      default:
                        return null;
                    }
                  };

                  const renderChart = () => {
                    switch(card.chartType) {
                      case "area":
                        return (
                          <AreaChartComponent
                            data={chartDataForDisplay(card.chartData)}
                            dataKey="value"
                            color={card.color}
                          />
                        );
                      case "bar":
                        return (
                          <BarChartComponent
                            data={card.chartData}
                            dataKey="value"
                            color={card.color}
                          />
                        );
                      case "line":
                        return (
                          <LineChartComponent
                            data={chartDataForDisplay(card.chartData)}
                            dataKey="value"
                            color={card.color}
                          />
                        );
                      default:
                        return null;
                    }
                  };

                  const isExpanded = expandedCards.has(card.title);

                  return (
                    <div 
                      key={card.title} 
                      className={`action-card ${isExpanded ? 'expanded' : ''}`}
                      // onClick={() => toggleCardExpansion(card.title)}
                    >
                      <div className="action-header-with-icon">
                        {getIcon(card.title)}
                        <div className="action-title-wrapper">
                          <div className="action-title">{card.title}</div>
                          <div className="action-desc">{card.desc}</div>
                        </div>
                        {/* <svg 
                          className={`card-expand-icon ${isExpanded ? 'expanded' : ''}`}
                          width="16" 
                          height="16" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        >
                          <polyline points="6 9 12 15 18 9"></polyline>
                        </svg> */}
                      </div>
                      {isExpanded && (
                        <div className="card-chart-container">
                          <ChartContainer>
                            {renderChart()}
                          </ChartContainer>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="right-column">
              {rightCards.map((card) => {
                const renderChart = () => {
                  switch(card.chartType) {
                    case "area":
                      return (
                        <AreaChartComponent
                          data={chartDataForDisplay(card.chartData)}
                          dataKey="value"
                          color={card.color}
                        />
                      );
                    case "bar":
                      return (
                        <BarChartComponent
                          data={card.chartData}
                          dataKey="value"
                          color={card.color}
                        />
                      );
                    case "line":
                      return (
                        <LineChartComponent
                          data={chartDataForDisplay(card.chartData)}
                          dataKey="value"
                          color={card.color}
                        />
                      );
                    default:
                      return null;
                  }
                };

                return (
                  <div key={card.title} className={`action-card ${card.title === "Total Sessions" ? "sessions-card" : ""}`}>
                    <div className="action-title-row">
                      <div className="action-title">{card.title}</div>
                      {card.title === "Total Sessions" && (
                        <div className="action-desc-inline">{card.desc}</div>
                      )}
                    </div>
                    {card.title !== "Total Sessions" && (
                      <div className="action-desc">{card.desc}</div>
                    )}
                    <div className="card-chart-container">
                      <ChartContainer>
                        {renderChart()}
                      </ChartContainer>
                    </div>
                    {card.title === "Total Sessions" && (
                      <>
                        <div className="action-subtitle">Latest 5 sessions</div>
                        <div className="top-messages-list">
                          {topSessionsForCard.map((message, index) => (
                            <div key={message.message_id} className="message-item">
                              <div className="message-header">
                                <div className="message-user-info">
                                  <div className="message-avatar">
                                    {getUserInitials(message.user_name)}
                                  </div>
                                  <div className="message-user-details">
                                    <div className="message-user-name">{message.user_name}</div>
                                    <div className="message-user-role">User ID: {message.user_id}</div>
                                  </div>
                                </div>
                                <div className="message-time">{formatTime(message.timestamp)}</div>
                              </div>
                              <div className="message-content-wrapper">
                                <div className="message-badges-left">
                                  <div className="message-type">{message.message_type}</div>
                                  <div className="message-category">{message.category}</div>
                                </div>
                                <div className="message-status-badge">
                                  <span className={`status-indicator ${message.status.toLowerCase()}`}>
                                    {message.status}
                                  </span>
                                </div>
                              </div>
                              <div className="message-text">{message.message_content}</div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            {/* <div className="full-width-row">
              {fullWidthCards.map((card) => {
                const renderChart = () => {
                  switch(card.chartType) {
                    case "area":
                      return (
                        <AreaChartComponent
                          data={chartDataForDisplay(card.chartData)}
                          dataKey="value"
                          color={card.color}
                        />
                      );
                    case "bar":
                      return (
                        <BarChartComponent
                          data={card.chartData}
                          dataKey="value"
                          color={card.color}
                        />
                      );
                    case "line":
                      return (
                        <LineChartComponent
                          data={chartDataForDisplay(card.chartData)}
                          dataKey="value"
                          color={card.color}
                        />
                      );
                    default:
                      return null;
                  }
                };

                return (
                  <div key={card.title} className="action-card sessions-card">
                    <div className="action-title-row">
                      <div className="action-title">{card.title}</div>
                      <div className="action-desc-inline">{card.desc}</div>
                    </div>
                    <div className="card-chart-container">
                      <ChartContainer>
                        {renderChart()}
                      </ChartContainer>
                    </div>
                    <div className="action-subtitle">Latest 5 sessions</div>
                    <div className="top-messages-list">
                      {topSessionsForCard.map((message, index) => (
                        <div key={message.message_id} className="message-item">
                          <div className="message-header">
                            <div className="message-user-info">
                              <div className="message-avatar">
                                {getUserInitials(message.user_name)}
                              </div>
                              <div className="message-user-details">
                                <div className="message-user-name">{message.user_name}</div>
                                <div className="message-user-role">User ID: {message.user_id}</div>
                              </div>
                            </div>
                            <div className="message-time">{formatTime(message.timestamp)}</div>
                          </div>
                          <div className="message-content-wrapper">
                            <div className="message-badges-left">
                              <div className="message-type">{message.message_type}</div>
                              <div className="message-category">{message.category}</div>
                            </div>
                            <div className="message-status-badge">
                              <span className={`status-indicator ${message.status.toLowerCase()}`}>
                                {message.status}
                              </span>
                            </div>
                          </div>
                          <div className="message-text">{message.message_content}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div> */}
            </>
            ) : (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                padding: '40px',
                color: '#9CA3AF',
                fontSize: '14px'
              }}>
                No data available for charts
              </div>
            )}
          </section>
        </div>
        </div>
        )}

        {/* Search Page */}
        {activeMenu === "search" && (
          <SearchSection onViewMessages={handleViewMessages} />
        )}

        {/* Sessions Table */}
        {activeMenu === "sessions" && (
          <SessionsTable
            paginatedSessions={paginatedSessions}
            currentPage={currentPage}
            sessionsData={sessionsData}
            itemsPerPage={itemsPerPage}
            sessionSearchQuery={sessionSearchQuery}
            onSessionSearchInput={handleSessionSearchInput}
            onPageChange={handlePageChange}
            onViewMessages={handleViewMessages}
            formatDate={formatDate}
            loadingSessions={loadingSessions}
            dateRangePicker={<DateRangePicker {...dateRange} />}
          />
        )}

        {/* Feedbacks Table */}
        {activeMenu === "feedbacks" && (
          <FeedbacksTable
            paginatedFeedbacks={paginatedFeedbacks}
            currentFeedbackPage={currentFeedbackPage}
            feedbacksData={feedbacksData}
            itemsPerPage={itemsPerPage}
            onPageChange={handleFeedbackPageChange}
            onViewFeedbackMessage={handleViewFeedbackMessage}
            onViewMessages={handleViewMessages}
            feedbackSearchQuery={feedbackSearchQuery}
            onFeedbackSearchInput={handleFeedbackSearchInput}
            formatDate={formatDate}
            loadingFeedbacks={loadingFeedbacks}
            dateRangePicker={<DateRangePicker {...dateRange} />}
          />
        )}

        {activeMenu === "semantic-settings" && (
          <SemanticSettings onClose={() => setActiveMenu("dashboard")} />
        )}

        {activeMenu === "access-manager" && (
          isAccessManagerEnabled() && getAdminAccess() ? (
            <AccessManagerPanel />
          ) : (
            <div className="admin-access-denied">
              <p>Access Denied. You do not have permission to view this panel.</p>
            </div>
          )
        )}

        {/* Feedback Message Modal / Violation Details Modal */}
        {showFeedbackMessageModal && selectedFeedbackMessage && (
          <div className="messages-modal-overlay" onClick={closeFeedbackMessageModal}>
            <div className="messages-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="messages-modal-header">
                <h3>
                  {selectedFeedbackMessage.violation_id 
                    ? `Violation Details - Violation ID: ${selectedFeedbackMessage.violation_id}`
                    : `Message Content - Feedback ID: ${selectedFeedbackMessage.feedback_id}`}
                </h3>
                <button className="close-modal-btn" onClick={closeFeedbackMessageModal}>
                  <IconClose width={24} height={24} />
                </button>
              </div>
              <div className="messages-modal-body">
                {selectedFeedbackMessage.violation_id ? (
                  // Violation Details View
                  <div className="feedback-message-details">
                    <div className="feedback-message-info">
                      <div className="info-row">
                        <span className="info-label">Violation ID:</span>
                        <span className="info-value">{selectedFeedbackMessage.violation_id}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Session ID:</span>
                        <span className="info-value">{selectedFeedbackMessage.session_id}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">User ID:</span>
                        <span className="info-value">{selectedFeedbackMessage.user_id}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Violation Types:</span>
                        <span className="info-value">
                          {Array.isArray(selectedFeedbackMessage.violation_types) 
                            ? selectedFeedbackMessage.violation_types.map((type, idx) => (
                                <span key={idx} className="violation-type-tag" style={{ marginRight: '4px' }}>
                                  {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                </span>
                              ))
                            : '-'}
                        </span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Violation Count:</span>
                        <span className="info-value">{selectedFeedbackMessage.violation_count || 0}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Timestamp:</span>
                        <span className="info-value">{formatDate(selectedFeedbackMessage.timestamp)}</span>
                      </div>
                    </div>
                    {selectedFeedbackMessage.user_query && (
                      <div className="feedback-message-content">
                        <h4>User Query:</h4>
                        <div className="full-message-content violation-user-query">
                          {selectedFeedbackMessage.user_query}
                        </div>
                      </div>
                    )}
                    {selectedFeedbackMessage.original_response && (
                      <div className="feedback-message-content">
                        <h4>Original Response:</h4>
                        <div className="full-message-content violation-original">
                          {selectedFeedbackMessage.original_response}
                        </div>
                      </div>
                    )}
                    {selectedFeedbackMessage.filtered_response && (
                      <div className="feedback-message-content">
                        <h4>Filtered Response:</h4>
                        <div className="full-message-content violation-filtered">
                          {selectedFeedbackMessage.filtered_response}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  // Feedback Message View
                  <div className="feedback-message-details">
                    <div className="feedback-message-info">
                      <div className="info-row">
                        <span className="info-label">Session ID:</span>
                        <span className="info-value">{selectedFeedbackMessage.session_id}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Message ID:</span>
                        <span className="info-value">{selectedFeedbackMessage.message_id}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">User ID:</span>
                        <span className="info-value">{selectedFeedbackMessage.user_id}</span>
                      </div>
                      <div className="info-row info-row-feedback-thumbs">
                        <span className="info-label">Feedback:</span>
                        <span className="info-value">
                          {renderFeedbackThumbs(selectedFeedbackMessage.feedback)}
                        </span>
                      </div>
                      <div className="info-row info-row-feedback-comment">
                        <span className="info-label">Comment:</span>
                        <span className="info-value admin-feedback-modal-comment">
                          {String(
                            selectedFeedbackMessage.feedback_comment ||
                              selectedFeedbackMessage.comment ||
                              ""
                          ).trim() || "-"}
                        </span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Feedback Time:</span>
                        <span className="info-value">{formatDate(selectedFeedbackMessage.feedback_time)}</span>
                      </div>
                    </div>
                    {/* <div className="feedback-message-content">
                      <h4>Message Content:</h4>
                      <div className="full-message-content">
                        {selectedFeedbackMessage.message_content}
                      </div>
                    </div> */}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Messages Modal */}
        {showMessagesModal && (
          <div className="messages-modal-overlay" onClick={closeMessagesModal}>
            <div className="messages-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="messages-modal-header chat-header">
                <div className="chat-header-left">
                  <div className="chat-header-icon">
                    <IconMessageBubbleStroke width={20} height={20} />
                  </div>
                  <div className="chat-header-info">
                    <h3 className="chat-header-title">AI Assistant</h3>
                    <div className="chat-header-status">
                      <span>Session: {selectedSessionId}</span>
                    </div>
                  </div>
                </div>
                <div className="chat-header-actions">
                  <button
                    type="button"
                    className="header-action-btn"
                    title="Download"
                    onClick={handleDownloadPDF}
                  >
                    <IconDownload width={18} height={18} />
                  </button>
                  <button
                    className="header-action-btn"
                    onClick={closeMessagesModal}
                    title="Close"
                  >
                    <IconClose width={18} height={18} />
                  </button>
                </div>
              </div>
              <div className="messages-search-bar">
                <IconSearch width={16} height={16} />
                <input
                  type="text"
                  placeholder="Search messages..."
                  value={messageSearchQuery}
                  onChange={(e) => setMessageSearchQuery(e.target.value)}
                  className="messages-search-input"
                />
                {messageSearchQuery && (
                  <button
                    className="search-clear-btn"
                    onClick={() => setMessageSearchQuery("")}
                    title="Clear search"
                  >
                    <IconX width={16} height={16} />
                  </button>
                )}
              </div>
              <div className="messages-modal-body chatbot-style">
                {loadingMessages ? (
                  <div className="loading-messages">Loading messages...</div>
                ) : sessionMessages.length === 0 ? (
                  <div className="no-messages">No messages found for this session.</div>
                ) : getFilteredMessages().length === 0 ? (
                  <div className="no-messages">No messages match your search.</div>
                ) : (
                  <div className="messages-list chatbot-messages">
                    <div
                      ref={messagesExportListRef}
                      style={{ display: "flex", flexDirection: "column" }}
                    >
                    {getFilteredMessages().map((message, index) => {
                      const hasCharts =
                        message.role === "assistant" &&
                        hasDisplayableCharts(message.charts);
                      const hasTable =
                        message.role === "assistant" &&
                        !message.isReasoning &&
                        message.queryType?.toLowerCase() !== "reasoning" &&
                        message.rawResults &&
                        message.rawResults.length > 0;
                      const hasWideContent = hasCharts || hasTable;

                      return (
                      <div
                        key={message.message_id || index}
                        style={{ marginBottom: "24px" }}
                        ref={(el) => {
                          if (!el) return;
                          messagesExportItemRefs.current[message.message_id || index] = el;
                        }}
                      >
                        {/* Message Bubble */}
                        <div
                          className={`chat-bubble ${message.role === "user" ? "user" : "bot"}${message.isReasoning ? " reasoning" : ""}${hasWideContent ? " has-wide-content" : ""}`}
                        >
                          <div>
                            <img
                              src={message.role === "user" ? profileIcon : botIcon}
                              alt={message.role === "user" ? "user icon" : "bot icon"}
                            />
                          </div>
                          <div className="chat-bubble-content-wrapper">
                            {/* Sub-query dropdown for reasoning mode */}
                            {message.role === "assistant" &&
                              (message.isReasoning ||
                                message.queryType?.toLowerCase() === "reasoning") &&
                              message.subQueries &&
                              message.subQueries.length > 0 && (
                                <div className="sub-query-dropdown-container">
                                  <div className="sub-query-label">Analysis Breakdown:</div>
                                  <div className="sub-query-dropdown-wrapper">
                                    <button
                                      type="button"
                                      className="sub-query-dropdown-btn selected"
                                      onClick={() =>
                                        setShowSubQueryDropdown((prev) => ({
                                          ...prev,
                                          [index]: !prev[index],
                                        }))
                                      }
                                    >
                                      <div className="selected-sub-query">
                                        <span className="sub-query-badge generic">FA</span>
                                        <span className="sub-query-text-label">
                                          Full Analysis Breakdown
                                        </span>
                                      </div>
                                      <IconChevronDown width={12} height={12} />
                                    </button>
                                    {showSubQueryDropdown[index] && (
                                      <div className="sub-query-dropdown-menu">
                                        {message.subQueries.map((subQuery) => (
                                          <div
                                            key={subQuery.index}
                                            className="sub-query-dropdown-item sub-query-dropdown-item-static"
                                          >
                                            <span className="sub-query-number">
                                              Q{subQuery.index}
                                            </span>
                                            <span className="sub-query-text">
                                              {subQuery.question}
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            {/* QA / Reasoning badge for bot messages */}
                            {message.role === "assistant" && message.queryType && (
                              <div className="bot-query-mode-badge-row">
                                <span className={`query-mode-indicator ${message.queryType.toLowerCase()}`}>
                                  {message.queryType.toLowerCase() === 'reasoning' ? (
                                    <>
                                      <TipsAndUpdatesRoundedIcon style={{ fontSize: 12, marginRight: 4 }} />
                                      Reasoning
                                    </>
                                  ) : (
                                    <>
                                      <QuestionAnswerRoundedIcon style={{ fontSize: 12, marginRight: 4 }} />
                                      Q/A
                                    </>
                                  )}
                                </span>
                              </div>
                            )}
                            {message.role === "assistant" &&
                            (message.isReasoning ||
                              message.queryType?.toLowerCase() === "reasoning") ? (
                              <div className="bot-message-content">
                                <MarkdownRenderer
                                  content={(message.content || "").replace(
                                    /<br\s*\/?>/gi,
                                    "\n"
                                  )}
                                  isBot={true}
                                />
                              </div>
                            ) : (
                              <p>
                                <HighlightText
                                  text={message.content}
                                  highlight={messageSearchQuery.trim()}
                                />
                              </p>
                            )}
                            {hasTable &&
                              renderDataTable(message.rawResults, index, message)}
                            {hasCharts && !hasTable && (
                              <div
                                className={`bot-message-stack${hasWideContent ? " has-wide-content" : ""}`}
                              >
                                {renderAdminMessageChartsPanel(message, index)}
                              </div>
                            )}
                            {message.role === "assistant" && message.sqlQuery && (
                              <div className="chat-data-wrapper">
                                <details className="sql-query-details sql-details-wrapper">
                                  <summary className="sql-summary-styled">
                                    <div className="sql-query-header">
                                      <div className="sql-query-icon">
                                        <IconSqlBracketsWhite width={16} height={16} />
                                      </div>
                                      <div className="sql-query-info">
                                        <div className="sql-query-label">
                                          Generated SQL Query
                                        </div>
                                        <div className="sql-query-hint">
                                          Click to expand and view the query
                                        </div>
                                      </div>
                                    </div>
                                    <button
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleCopySQL(message.sqlQuery, index);
                                      }}
                                      className="btn-copy-sql-styled"
                                      type="button"
                                    >
                                      {copiedSqlIndex === index ? (
                                        <>
                                          <IconCheck width={14} height={14} />
                                          Copied!
                                        </>
                                      ) : (
                                        <>
                                          <IconCopy width={14} height={14} />
                                          Copy
                                        </>
                                      )}
                                    </button>
                                  </summary>
                                  <div className="sql-code-wrapper">
                                    <pre
                                      className="sql-code-pre"
                                      dangerouslySetInnerHTML={{
                                        __html: highlightSql(message.sqlQuery),
                                      }}
                                    />
                                  </div>
                                </details>
                              </div>
                            )}
                            {message.role === "assistant" &&
                              message.feedback !== null &&
                              message.feedback !== undefined && (
                              <div className="message-feedback-inline">
                                {renderFeedbackThumbs(message.feedback)}
                                <span className="feedback-comment-line">
                                  <span className="feedback-comment-label">Comment:</span>
                                  <span className="feedback-comment">
                                    {(message.comment || message.feedback_comment) ? (
                                      <HighlightText
                                        text={(
                                          message.comment ||
                                          message.feedback_comment ||
                                          ""
                                        ).trim()}
                                        highlight={messageSearchQuery.trim()}
                                      />
                                    ) : (
                                      "-"
                                    )}
                                  </span>
                                </span>
                              </div>
                            )}
                            <span className="time">
                              <span className="color-000000">
                                {message.role === "user" ? selectedUserId : "Bot:"}{" "}
                              </span>
                              <span>{formatTime(message.timestamp)}</span>
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                    })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Full View Chart Modal (Show Message Popup) */}
        {fullViewMessageChart &&
          fullViewMessageChart.data &&
          fullViewMessageChart.data.length > 0 &&
          (() => {
            const modalChartKey =
              fullViewMessageChart._chartKey ?? "admin-msg-modal";
            const fullViewPointCount = fullViewMessageChart.data.length;
            const fullViewChartType = resolveChatChartType(
              fullViewMessageChart,
              msgChartTypeOverrides[modalChartKey] ??
                fullViewMessageChart._selectedType,
              fullViewPointCount
            );

            return (
              <EmcureModalShell
                onClose={() => setFullViewMessageChart(null)}
                title={fullViewMessageChart.title || "Chart"}
                headerExtra={
                  <div
                    className="chart-type-dropdown-wrapper"
                    style={{ position: "relative" }}
                  >
                    <button
                      type="button"
                      className="chart-action-btn chart-type-toggle-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenMsgChartTypeDropdown((prev) =>
                          prev === modalChartKey ? null : modalChartKey
                        );
                      }}
                      title="Change chart type"
                    >
                      {fullViewChartType === "pie" && (
                        <IconChartPie width={14} height={14} />
                      )}
                      {fullViewChartType === "bar" && (
                        <IconChartBar width={14} height={14} />
                      )}
                      {fullViewChartType === "line" && (
                        <IconChartLine width={14} height={14} />
                      )}
                      <span style={{ textTransform: "capitalize" }}>
                        {fullViewChartType}
                      </span>
                      <IconChevronDownBold width={10} height={10} />
                    </button>
                    {openMsgChartTypeDropdown === modalChartKey && (
                      <div
                        className="chart-type-dropdown-menu"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {getChartTypeOptionsForPointCount(fullViewPointCount).map(
                          ({ type: t, label }) => {
                            const icon =
                              t === "pie" ? (
                                <IconChartPie width={14} height={14} />
                              ) : t === "line" ? (
                                <IconChartLine width={14} height={14} />
                              ) : (
                                <IconChartBar width={14} height={14} />
                              );
                            return (
                              <button
                                key={t}
                                type="button"
                                className={`chart-type-dropdown-item${
                                  fullViewChartType === t ? " active" : ""
                                }`}
                                onClick={() => {
                                  setMsgChartTypeOverrides((prev) => ({
                                    ...prev,
                                    [modalChartKey]: t,
                                  }));
                                  setOpenMsgChartTypeDropdown(null);
                                }}
                              >
                                {icon}
                                {label}
                              </button>
                            );
                          }
                        )}
                      </div>
                    )}
                  </div>
                }
              >
                <div className="chart-modal-body">
                  <EmcureModalChartBody
                    chart={fullViewMessageChart}
                    effectiveType={fullViewChartType}
                    idPrefix="admin-msg-fullview"
                    formatDateAxis={formatDateValue}
                    parseTooltipNumber={parseTooltipMetricValue}
                  />
                </div>
              </EmcureModalShell>
            );
          })()}

        <SqlResultDataTableFullViewModal
          open={fullViewTable}
          onClose={() => {
            setFullViewTable(null);
            setOpenTableExportDropdown(null);
          }}
          variant="admin"
          formatScalarForDisplay={safeText}
          admin={(() => {
            const fvIdx = parseTableMessageIndex(fullViewTable?.tableKey);
            const fvMsg =
              fullViewTable && !Number.isNaN(fvIdx) ? sessionMessages[fvIdx] : null;
            const fvFooter = fullViewTable
              ? resolveTableFooterState({
                  rawResults: fullViewTable.data ?? [],
                  dataSummary: fvMsg?.dataSummary ?? {},
                  paginationInfo: fvMsg?.pagination ?? {},
                  storedTotalCount: fullViewTable.totalCount ?? fvMsg?.totalCount,
                  rawResultsBuffer: fvMsg?.rawResultsBuffer,
                })
              : null;
            return {
            getCsvExportOptions,
            onPickExportOption: (exportType) =>
              handleExportTableCSV({
                exportType,
                messageId: fullViewTable?.messageId,
                sessionId: fullViewTable?.sessionId ?? selectedSessionId,
              }),
            isExportMenuOpen: openTableExportDropdown === "fullview",
            onToggleExportMenu: () => {
              setOpenTableExportDropdown(
                openTableExportDropdown === "fullview" ? null : "fullview"
              );
            },
            isExportOptionLoading: (exportType) =>
              exportCsvLoadingKey === `fullview:${exportType}`,
            messageId: fullViewTable?.messageId,
            sessionId: fullViewTable?.sessionId ?? selectedSessionId,
            totalCount: fvFooter?.totalCount ?? fullViewTable?.totalCount,
            exportDisabled: fullViewTableLoading,
            showLoadMore: Boolean(fullViewTable?.sqlQuery && fvFooter?.hasMore),
            loadMoreLoading: fullViewTableLoading,
            onLoadMore: handleAdminFullViewTableLoadMore,
            loadMoreCount: fvFooter?.loadMoreCount,
            sort: buildTableSortProps(
              "fullview",
              fvIdx,
              fullViewTable?.messageId,
              fullViewTable?.sessionId ?? selectedSessionId,
              {
                queryRowCount: fvFooter?.totalCount ?? fullViewTable?.totalCount,
                loadedCount: fullViewTable?.data?.length ?? 0,
              }
            ),
            };
          })()}
        />
            </div>
        </section>
      </div>
      </main>
    </div>
  );
};

export default AdminDashboard;


